import { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  Compass,
  Sliders,
  Sparkles,
  Package,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  ChevronRight,
  Flame,
  Droplets,
  Shield,
  Moon,
  Search,
  X,
  Filter,
  Swords,
  Clock,
  Layers,
  Crown,
  Wine,
  Wind,
  Sparkle,
  Target,
  MousePointer,
  Crosshair,
  MapPin,
  Briefcase,
} from 'lucide-react'
import { api } from '@/lib/api'
import { formatPrice, localizedName } from '@/lib/format'
import { haptic } from '@/lib/telegram'
import { useAppStore } from '@/lib/store'
import { usePageTitle } from '@/lib/usePageTitle'
import { ScentCapsuleBuilder } from '@/components/ScentCapsuleBuilder'

interface PerfumeVector {
  freshness: number
  sharpness: number
  sillage: number
  sweetness: number
  formality: number
}

interface PyramidStages {
  top: string[]
  heart: string[]
  base: string[]
}

interface ScentProduct {
  id: number
  slug: string
  name_uk: string
  name_en?: string
  price_usd?: number
  price_uah?: number
  photo_url?: string | null
  photo_source_url?: string | null
  vector: PerfumeVector
  brand?: string | null
  perfumer?: string | null
  gender?: string[]
  seasons?: string[]
  dominant_chords?: string[]
  key_notes?: string[]
  all_notes?: string[]
  pyramid?: PyramidStages
  match_reason?: string
}

interface CompassResponse {
  total: number
  products: ScentProduct[]
}

type ProjectionKey =
  | 'freshness-sillage'
  | 'freshness-sharpness'
  | 'sweetness-formality'
  | 'sharpness-sillage'

interface AxisMeta {
  nameUk: string
  nameEn: string
  minUk: string
  minEn: string
  maxUk: string
  maxEn: string
}

const AXIS_MAP: Record<keyof PerfumeVector, AxisMeta> = {
  freshness: {
    nameUk: 'Свіжість & Температура',
    nameEn: 'Freshness & Temperature',
    minUk: 'Крижаний / Цитрус / Море',
    minEn: 'Icy / Citrus / Aquatic',
    maxUk: 'Гарячий Схід / Уд / Амбра',
    maxEn: 'Hot Oriental / Oud / Amber',
  },
  sharpness: {
    nameUk: 'Різкість & Текстура',
    nameEn: 'Sharpness & Texture',
    minUk: 'Оксамит / Пудра / Мускус',
    minEn: 'Velvet / Powdery / Musk',
    maxUk: 'Пряний / Шкіра / Дим / Тютюн',
    maxEn: 'Spicy / Leather / Smoke',
  },
  sillage: {
    nameUk: 'Шлейф & Дифузність',
    nameEn: 'Sillage & Presence',
    minUk: 'Інтимний / Близько до шкіри',
    minEn: 'Intimate / Skin-close',
    maxUk: 'Ядерний / Монструозний шлейф',
    maxEn: 'Nuclear / Monumental',
  },
  sweetness: {
    nameUk: 'Солодкість vs Сухість',
    nameEn: 'Sweetness vs Dryness',
    minUk: 'Сухий / Мінеральний / Деревний',
    minEn: 'Dry / Mineral / Woody',
    maxUk: 'Гурман / Мед / Карамель / Ваніль',
    maxEn: 'Gourmand / Honey / Vanilla',
  },
  formality: {
    nameUk: 'Формат & Нагода',
    nameEn: 'Formality & Occasion',
    minUk: 'Денний / Офіс / Кежуал',
    minEn: 'Daily / Office / Casual',
    maxUk: 'Вечірній / Gala / Ніч',
    maxEn: 'Evening / Gala / Night',
  },
}

export interface QuadrantPreset {
  id: string
  nameUk: string
  nameEn: string
  nameRu: string
  descUk: string
  descEn: string
  descRu: string
  icon: string
  x: number
  y: number
}

export const QUADRANT_PRESETS: Record<ProjectionKey, QuadrantPreset[]> = {
  'freshness-sillage': [
    {
      id: 'fresh-intimate',
      icon: '❄️',
      nameUk: 'Інтимна свіжість',
      nameEn: 'Intimate Fresh',
      nameRu: 'Интимная свежесть',
      descUk: 'Делікатний цитрус, біла сорочка, чистий офіс',
      descEn: 'Delicate citrus, clean linen, office skin-scent',
      descRu: 'Деликатный цитрус, белая рубашка, чистый офис',
      x: 0.25,
      y: 0.25,
    },
    {
      id: 'fresh-nuclear',
      icon: '🌊',
      nameUk: 'Вибуховий шлейф свіжості',
      nameEn: 'Oceanic Sillage',
      nameRu: 'Взрывной свежий шлейф',
      descUk: 'Морський озон, мінерали, кілометровий шлейф',
      descEn: 'Marine ozone, crisp salt, monumental trail',
      descRu: 'Морской озон, минералы, километровый шлейф',
      x: 0.25,
      y: 0.75,
    },
    {
      id: 'warm-subtle',
      icon: '🪵',
      nameUk: 'Кашемірове тепло',
      nameEn: 'Cashmere Warmth',
      nameRu: 'Кашемировое тепло',
      descUk: 'Сухий ветивер, замша, спокійний особистий затишок',
      descEn: 'Dry vetiver, suede, calm personal comfort',
      descRu: 'Сухой ветивер, замша, спокойный личный уют',
      x: 0.75,
      y: 0.25,
    },
    {
      id: 'warm-nuclear',
      icon: '👑',
      nameUk: 'Східний монумент',
      nameEn: 'Regal Oriental',
      nameRu: 'Восточный монумент',
      descUk: 'Уд, гаряча амбра, смоли, шафран, заповнює залу',
      descEn: 'Oud, molten amber, dark resins, room-filling party',
      descRu: 'Уд, горячая амбра, смолы, шафран, заполняет зал',
      x: 0.75,
      y: 0.75,
    },
  ],
  'freshness-sharpness': [
    {
      id: 'fresh-soft',
      icon: '🤍',
      nameUk: 'Оксамитова свіжість',
      nameEn: 'Velvet Fresh',
      nameRu: 'Бархатная свежесть',
      descUk: 'Пудровий мускус, ірис, шовковий ніжний дотик',
      descEn: 'Powdery musk, soft iris, gentle silk touch',
      descRu: 'Пудровый мускус, ирис, шелковое нежное касание',
      x: 0.25,
      y: 0.25,
    },
    {
      id: 'fresh-sharp',
      icon: '⚡',
      nameUk: 'Іскристий кристал',
      nameEn: 'Mineral Ice',
      nameRu: 'Искристый кристалл',
      descUk: 'Колючий цитрус, мінеральний озон, бадьорий драйв',
      descEn: 'Crisp citrus, mineral crystals, invigorating drive',
      descRu: 'Колючий цитрус, минеральный озон, бодрящий драйв',
      x: 0.25,
      y: 0.75,
    },
    {
      id: 'warm-soft',
      icon: '🍯',
      nameUk: 'Медова амбра',
      nameEn: 'Honeyed Amber',
      nameRu: 'Медовая амбра',
      descUk: 'Округла ваніль, м\'який віск, ніжне густе тепло',
      descEn: 'Rounded vanilla, warm beeswax, sweet comforting glow',
      descRu: 'Округлая ваниль, мягкий воск, нежное густое тепло',
      x: 0.75,
      y: 0.25,
    },
    {
      id: 'warm-sharp',
      icon: '🔥',
      nameUk: 'Дим & Чорна шкіра',
      nameEn: 'Smoke & Black Leather',
      nameRu: 'Дым & Черная кожа',
      descUk: 'Смолистий ладан, дьоготь, тютюн, пряний перець',
      descEn: 'Resinous incense, birch tar, dry tobacco, black pepper',
      descRu: 'Смолистый ладан, деготь, табак, пряный перец',
      x: 0.75,
      y: 0.75,
    },
  ],
  'sweetness-formality': [
    {
      id: 'dry-casual',
      icon: '🌿',
      nameUk: 'Сухий щоденний кежуал',
      nameEn: 'Dry Casual',
      nameRu: 'Сухой повседневный кэжуал',
      descUk: 'Трави, кедр, невагома прогулянка, джинси й біла футболка',
      descEn: 'Herbs, cedar, lightweight daily walk, jeans & tee',
      descRu: 'Травы, кедр, невесомая прогулка, джинсы и футболка',
      x: 0.25,
      y: 0.25,
    },
    {
      id: 'dry-formal',
      icon: '👔',
      nameUk: 'Суворий вечірній статус',
      nameEn: 'Diplomatic Tuxedo',
      nameRu: 'Строгий вечерний статус',
      descUk: 'Гіркі пачулі, сухий ладан, безкомпромісний авторитет',
      descEn: 'Bitter patchouli, dry incense, uncompromising authority',
      descRu: 'Горькие пачули, сухой ладан, бескомпромиссный авторитет',
      x: 0.25,
      y: 0.75,
    },
    {
      id: 'sweet-cozy',
      icon: '🍦',
      nameUk: 'Затишний десерт',
      nameEn: 'Cozy Gourmand',
      nameRu: 'Уютный десерт',
      descUk: 'Боби тонка, горіхове праліне, капучино в теплий день',
      descEn: 'Tonka bean, hazelnut praline, daytime sweet latte',
      descRu: 'Бобы тонка, ореховое пралине, капучино в теплый день',
      x: 0.75,
      y: 0.25,
    },
    {
      id: 'sweet-gala',
      icon: '✨',
      nameUk: 'Богемна Gala-ніч',
      nameEn: 'Bohemian Gala',
      nameRu: 'Богемная Gala-ночь',
      descUk: 'П\'яний ром, карамельний шлейф, магнетична спокуса',
      descEn: 'Boozy rum, caramel sillage, magnetic sensual evening',
      descRu: 'Пьяный ром, карамельный шлейф, магнетическое соблазнение',
      x: 0.75,
      y: 0.75,
    },
  ],
  'sharpness-sillage': [
    {
      id: 'soft-intimate',
      icon: '☁️',
      nameUk: 'Пудрова хмаринка',
      nameEn: 'Powdery Cloud',
      nameRu: 'Пудровое облако',
      descUk: 'Інтимний ірис, білий мускус, шовковий кокон',
      descEn: 'Intimate iris, white musk, silk cocoon',
      descRu: 'Интимный ирис, белый мускус, шелковый кокон',
      x: 0.25,
      y: 0.25,
    },
    {
      id: 'soft-sillage',
      icon: '🌸',
      nameUk: 'Дифузний оксамит',
      nameEn: 'Velvet Radiance',
      nameRu: 'Диффузный бархат',
      descUk: 'М\'які пелюстки троянди, мускусна аура на всю кімнату',
      descEn: 'Soft rose petals, expansive musk radiance across the room',
      descRu: 'Мягкие лепестки розы, мускусная аура на всю комнату',
      x: 0.25,
      y: 0.75,
    },
    {
      id: 'sharp-intimate',
      icon: '🗡️',
      nameUk: 'Гострий особистий акцент',
      nameEn: 'Crisp Minimal',
      nameRu: 'Острый личный акцент',
      descUk: 'Сухий чорний перець, графіт, характер без нав\'язливості',
      descEn: 'Dry black pepper, graphite, crisp personal character',
      descRu: 'Сухой черный перец, графит, характер без навязчивости',
      x: 0.75,
      y: 0.25,
    },
    {
      id: 'sharp-nuclear',
      icon: '💥',
      nameUk: 'Ядерний перфоманс',
      nameEn: 'Nuclear Trail',
      nameRu: 'Ядерный перформанс',
      descUk: 'Шафран, смоли, шкіра, неможливо пройти повз',
      descEn: 'Saffron, dark resins, heavy leather, impossible to ignore',
      descRu: 'Шафран, смолы, кожа, невозможно пройти мимо',
      x: 0.75,
      y: 0.75,
    },
  ],
}

function getQuadrantWatermarks(projection: ProjectionKey, lang: string) {
  const quads = QUADRANT_PRESETS[projection] || QUADRANT_PRESETS['freshness-sillage']
  const getName = (q: QuadrantPreset) => (lang === 'en' ? q.nameEn : lang === 'ru' ? q.nameRu : q.nameUk)
  return {
    q1: `${quads[0].icon} ${getName(quads[0])}`,
    q2: `${quads[1].icon} ${getName(quads[1])}`,
    q3: `${quads[2].icon} ${getName(quads[2])}`,
    q4: `${quads[3].icon} ${getName(quads[3])}`,
  }
}

const ARCHETYPES = [
  {
    id: 'ganymede',
    nameUk: '🌌 Мінеральний Космос',
    nameEn: '🌌 Mineral Cosmos',
    descUk: 'Тип Ganymede: замша, шафран, акігалавуд',
    descEn: 'Ganymede vibe: suede, saffron, akigalawood',
    icon: Sparkle,
    color: '#06b6d4',
    vector: { freshness: 0.38, sharpness: 0.72, sillage: 0.94, sweetness: 0.18, formality: 0.7 },
  },
  {
    id: 'baccarat',
    nameUk: '✨ Бурштинова Вуаль',
    nameEn: '✨ Ambergris Veil',
    descUk: 'Тип Baccarat 540: амброксан, карамельний кедр',
    descEn: 'Baccarat 540 vibe: ambergris, caramel cedar',
    icon: Crown,
    color: '#f59e0b',
    vector: { freshness: 0.75, sharpness: 0.42, sillage: 0.92, sweetness: 0.85, formality: 0.82 },
  },
  {
    id: 'aventus',
    nameUk: '👑 Імперський Шипр',
    nameEn: '👑 Imperial Chypre',
    descUk: 'Тип Aventus: ананас, береза, мох, ветивер',
    descEn: 'Aventus vibe: pineapple, birch smoke, oakmoss',
    icon: Wind,
    color: '#10b981',
    vector: { freshness: 0.32, sharpness: 0.78, sillage: 0.82, sweetness: 0.35, formality: 0.75 },
  },
  {
    id: 'black_afgano',
    nameUk: '🪵 Темний Шаманізм',
    nameEn: '🪵 Dark Shamanism',
    descUk: 'Тип Black Afgano / Interlude: смоли, ладан, уд',
    descEn: 'Interlude vibe: resins, dark smoke, frankincense',
    icon: Flame,
    color: '#f97316',
    vector: { freshness: 0.88, sharpness: 0.86, sillage: 0.88, sweetness: 0.32, formality: 0.92 },
  },
  {
    id: 'tobacco_vanille',
    nameUk: '🥃 Богемний Тютюн',
    nameEn: '🥃 Boozy Tobacco',
    descUk: 'Тип Tobacco Vanille: ром, тютюновий лист, ваніль',
    descEn: 'Tobacco Vanille vibe: rum, dried fruit, rich spices',
    icon: Wine,
    color: '#d97706',
    vector: { freshness: 0.82, sharpness: 0.65, sillage: 0.84, sweetness: 0.88, formality: 0.86 },
  },
  {
    id: 'ocean',
    nameUk: '🌊 Крижана Акватика',
    nameEn: '🌊 Icy Marine',
    descUk: 'Цитрус, морська сіль, озон, чистий лемонграс',
    descEn: 'Icy citrus, mineral salt spray, fresh office-ready',
    icon: Droplets,
    color: '#38bdf8',
    vector: { freshness: 0.12, sharpness: 0.45, sillage: 0.55, sweetness: 0.22, formality: 0.2 },
  },
  {
    id: 'velvet',
    nameUk: '🤍 Оксамитова Шкіра',
    nameEn: '🤍 Velvet Skin',
    descUk: 'Інтимний пудровий ірис, білий мускус, кашмеран',
    descEn: 'Intimate powdery iris, clean white musk, second skin',
    icon: Shield,
    color: '#a855f7',
    vector: { freshness: 0.45, sharpness: 0.15, sillage: 0.25, sweetness: 0.48, formality: 0.35 },
  },
]

function calculateGaussianMatch(
  vectorA: PerfumeVector,
  vectorB: PerfumeVector,
  sigma: number = 0.42,
): number {
  const axes: (keyof PerfumeVector)[] = [
    'freshness',
    'sharpness',
    'sillage',
    'sweetness',
    'formality',
  ]
  let sumSq = 0
  axes.forEach((a) => {
    const diff = (vectorA[a] ?? 0.5) - (vectorB[a] ?? 0.5)
    sumSq += diff * diff
  })
  const dist = Math.sqrt(sumSq)
  const similarity = Math.exp(-((dist ** 2) / (2 * (sigma ** 2))))
  return Math.round(similarity * 100)
}

export function ScentFinderPage() {
  const lang = useAppStore((s) => s.lang)
  usePageTitle(lang === 'en' ? 'Scent Universe & Compass' : 'Матриця & Компас Ароматів')
  const { location } = useRouterState()

  type ScentFinderTab = 'compass' | 'matrix' | 'radar' | 'pyramid' | 'versus' | 'capsule'

  const currentTabFromUrl = useMemo<ScentFinderTab>(() => {
    const searchStr = location?.searchStr || (typeof window !== 'undefined' ? window.location.search : '')
    const params = new URLSearchParams(searchStr)
    const tabParam = params.get('tab')
    if (tabParam === 'capsule') return 'capsule'
    if (tabParam === 'versus') return 'versus'
    if (tabParam === 'pyramid') return 'pyramid'
    if (tabParam === 'radar') return 'radar'
    if (tabParam === 'matrix') return 'matrix'
    return 'compass'
  }, [location?.searchStr])

  const [overrideTab, setOverrideTab] = useState<ScentFinderTab | null>(null)
  const activeTab = overrideTab ?? currentTabFromUrl

  const handleTabChange = (tab: ScentFinderTab) => {
    haptic('light')
    setOverrideTab(tab)
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.searchParams.set('tab', tab)
      window.history.replaceState({}, '', url.toString())
    }
  }

  const isDiscoveryTab = activeTab === 'compass' || activeTab === 'radar' || activeTab === 'matrix'
  const [projection, setProjection] = useState<ProjectionKey>('freshness-sillage')
  const [targetVector, setTargetVector] = useState<PerfumeVector>({
    freshness: 0.5,
    sharpness: 0.5,
    sillage: 0.5,
    sweetness: 0.45,
    formality: 0.5,
  })
  const [hoveredProduct, setHoveredProduct] = useState<ScentProduct | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<ScentProduct | null>(null)

  // Connoisseur: Versus Battle Product
  const [versusProduct, setVersusProduct] = useState<ScentProduct | null>(null)

  // Connoisseur: Note Synergy Multi-select Filter
  const [selectedNotes, setSelectedNotes] = useState<string[]>([])

  // Connoisseur: Time Evolution Stage ('top' = 0-15m, 'heart' = 1-3h, 'base' = 4-8h+)
  const [pyramidStage, setPyramidStage] = useState<'top' | 'heart' | 'base'>('heart')

  // Reference perfume & Search
  const [referencePerfume, setReferencePerfume] = useState<ScentProduct | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearchFocused, setIsSearchFocused] = useState(false)

  // Filter chips
  const [genderFilter, setGenderFilter] = useState<'all' | 'female' | 'male' | 'unisex'>('all')
  const [seasonFilter, setSeasonFilter] = useState<'all' | 'warm' | 'cold'>('all')

  // Compass Map Zoom & Pan State
  const [zoom, setZoom] = useState(1.0)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 })

  // Compass Area-First vs Inspect Mode & Dragging State
  const [compassMode, setCompassMode] = useState<'area' | 'inspect'>('area')
  const [isDraggingBeacon, setIsDraggingBeacon] = useState(false)
  const lastHapticQuadrantRef = useRef<string>('')

  // Radar Interactive Drag State
  const [draggingAxis, setDraggingAxis] = useState<keyof PerfumeVector | null>(null)

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const radarRef = useRef<HTMLCanvasElement | null>(null)

  const { data } = useQuery<CompassResponse>({
    queryKey: ['scent-compass-products'],
    queryFn: () => api.get<CompassResponse>('/scent-finder/compass'),
    staleTime: 300_000,
  })

  const products = useMemo(() => data?.products || [], [data?.products])

  const [axisXKey, axisYKey] = projection.split('-') as [keyof PerfumeVector, keyof PerfumeVector]
  const axisX = AXIS_MAP[axisXKey]
  const axisY = AXIS_MAP[axisYKey]

  const quadrantPresets = useMemo(
    () => QUADRANT_PRESETS[projection] || QUADRANT_PRESETS['freshness-sillage'],
    [projection],
  )

  const activeQuadrant = useMemo(() => {
    const tx = targetVector[axisXKey] ?? 0.5
    const ty = targetVector[axisYKey] ?? 0.5
    const isLeft = tx < 0.5
    const isTop = ty < 0.5
    if (isLeft && isTop) return quadrantPresets[0]
    if (isLeft && !isTop) return quadrantPresets[1]
    if (!isLeft && isTop) return quadrantPresets[2]
    return quadrantPresets[3]
  }, [targetVector, axisXKey, axisYKey, quadrantPresets])

  const searchSuggestions = useMemo(() => {
    if (!searchQuery.trim()) return []
    const q = searchQuery.toLowerCase().trim()
    return products
      .filter((p) => {
        const name = (p.name_uk + ' ' + (p.name_en || '') + ' ' + (p.brand || '')).toLowerCase()
        return name.includes(q)
      })
      .slice(0, 6)
  }, [products, searchQuery])

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (genderFilter !== 'all' && p.gender && p.gender.length > 0) {
        const gStr = p.gender.join(' ').toLowerCase()
        if (genderFilter === 'female' && !gStr.includes('жіноч') && !gStr.includes('female')) return false
        if (genderFilter === 'male' && !gStr.includes('чоловіч') && !gStr.includes('мускульн') && !gStr.includes('male')) return false
        if (genderFilter === 'unisex' && !gStr.includes('унісекс') && !gStr.includes('unisex')) return false
      }
      if (seasonFilter !== 'all' && p.seasons && p.seasons.length > 0) {
        const sStr = p.seasons.join(' ').toLowerCase()
        if (seasonFilter === 'warm' && !sStr.includes('літ') && !sStr.includes('весн') && !sStr.includes('summer') && !sStr.includes('spring')) return false
        if (seasonFilter === 'cold' && !sStr.includes('осін') && !sStr.includes('зим') && !sStr.includes('autumn') && !sStr.includes('winter')) return false
      }
      if (selectedNotes.length > 0) {
        const pNotes = (p.all_notes || p.key_notes || []).map((n) => n.toLowerCase())
        const hasAllNotes = selectedNotes.every((sn) =>
          pNotes.some((pn) => pn.includes(sn.toLowerCase())),
        )
        if (!hasAllNotes) return false
      }
      return true
    })
  }, [products, genderFilter, seasonFilter, selectedNotes])

  const scoredProducts = useMemo(() => {
    return filteredProducts
      .map((p) => ({
        ...p,
        matchPct: calculateGaussianMatch(p.vector, targetVector),
      }))
      .sort((a, b) => b.matchPct - a.matchPct)
  }, [filteredProducts, targetVector])

  const zoneMatchCount = useMemo(() => {
    const tx = targetVector[axisXKey] ?? 0.5
    const ty = targetVector[axisYKey] ?? 0.5
    return filteredProducts.filter((p) => {
      const px = p.vector[axisXKey] ?? 0.5
      const py = p.vector[axisYKey] ?? 0.5
      return Math.hypot(px - tx, py - ty) <= 0.28
    }).length
  }, [filteredProducts, targetVector, axisXKey, axisYKey])

  const nearestProductToTarget = useMemo<ScentProduct | null>(() => {
    if (!filteredProducts.length) return null
    const tx = targetVector[axisXKey] ?? 0.5
    const ty = targetVector[axisYKey] ?? 0.5
    let closest: ScentProduct | null = null
    let minD = Infinity
    filteredProducts.forEach((p) => {
      const px = p.vector[axisXKey] ?? 0.5
      const py = p.vector[axisYKey] ?? 0.5
      const d = Math.hypot(px - tx, py - ty)
      if (d < minD) {
        minD = d
        closest = p
      }
    })
    return closest
  }, [filteredProducts, targetVector, axisXKey, axisYKey])

  const handleSelectReference = (prod: ScentProduct) => {
    setReferencePerfume(prod)
    setTargetVector({ ...prod.vector })
    setSelectedProduct(prod)
    setSearchQuery('')
    setIsSearchFocused(false)
    const W = canvasRef.current?.width || 560
    const H = canvasRef.current?.height || 560
    const px = (prod.vector[axisXKey] ?? 0.5) * W
    const py = (prod.vector[axisYKey] ?? 0.5) * H
    setPan({ x: (W / 2 - px), y: (H / 2 - py) })
    setZoom(1.8)
    haptic('success')
  }

  const handleClearReference = () => {
    setReferencePerfume(null)
    haptic('light')
  }

  // Reset map view
  const handleResetZoom = useCallback(() => {
    setZoom(1.0)
    setPan({ x: 0, y: 0 })
  }, [])

  // ─────────────────────────────────────────────────────────────
  // 1. CANVAS DRAWING (2D COMPASS MAP WITH ZOOM & PAN)
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'compass') return
    const cv = canvasRef.current
    if (!cv) return
    const ctx = cv.getContext('2d')
    if (!ctx) return

    const W = cv.width
    const H = cv.height
    ctx.clearRect(0, 0, W, H)

    ctx.save()

    // Apply Zoom & Pan transform around center
    ctx.translate(W / 2 + pan.x, H / 2 + pan.y)
    ctx.scale(zoom, zoom)
    ctx.translate(-W / 2, -H / 2)

    // Quadrant background gradients
    const zones = [
      { x: 0, y: 0, w: 0.5, h: 0.5, c: 'rgba(56,189,248,0.06)' },
      { x: 0.5, y: 0, w: 0.5, h: 0.5, c: 'rgba(74,222,128,0.06)' },
      { x: 0, y: 0.5, w: 0.5, h: 0.5, c: 'rgba(192,132,252,0.06)' },
      { x: 0.5, y: 0.5, w: 0.5, h: 0.5, c: 'rgba(200,169,110,0.09)' },
    ]
    zones.forEach((z) => {
      ctx.fillStyle = z.c
      ctx.fillRect(z.x * W, z.y * H, z.w * W, z.h * H)
    })

    // Quadrant watermarks in 4 corners
    const watermarks = getQuadrantWatermarks(projection, lang)
    const wmFs = Math.round(Math.max(10, (W * 0.024) / Math.sqrt(zoom)))
    ctx.font = `600 ${wmFs}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`
    ctx.fillStyle = 'rgba(255,255,255,0.18)'
    // Q1 (Top-Left): x 0..0.5, y 0..0.5
    ctx.textAlign = 'left'
    ctx.textBaseline = 'top'
    ctx.fillText(watermarks.q1, W * 0.04, H * 0.06)
    // Q2 (Bottom-Left): x 0..0.5, y 0.5..1.0
    ctx.textBaseline = 'bottom'
    ctx.fillText(watermarks.q2, W * 0.04, H * 0.94)
    // Q3 (Top-Right): x 0.5..1.0, y 0..0.5
    ctx.textAlign = 'right'
    ctx.textBaseline = 'top'
    ctx.fillText(watermarks.q3, W * 0.96, H * 0.06)
    // Q4 (Bottom-Right): x 0.5..1.0, y 0.5..1.0
    ctx.textBaseline = 'bottom'
    ctx.fillText(watermarks.q4, W * 0.96, H * 0.94)

    // Grid lines
    ctx.strokeStyle = 'rgba(255,255,255,0.05)'
    ctx.lineWidth = 1 / zoom
    for (let i = 1; i <= 9; i++) {
      ctx.beginPath()
      ctx.moveTo((i / 10) * W, 0)
      ctx.lineTo((i / 10) * W, H)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(0, (i / 10) * H)
      ctx.lineTo(W, (i / 10) * H)
      ctx.stroke()
    }

    // Center Cross
    ctx.strokeStyle = 'rgba(200,169,110,0.35)'
    ctx.lineWidth = 1.5 / zoom
    ctx.beginPath()
    ctx.moveTo(W / 2, 0)
    ctx.lineTo(W / 2, H)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(0, H / 2)
    ctx.lineTo(W, H / 2)
    ctx.stroke()

    // Axis labels
    const fs = Math.round(Math.max(10, (W * 0.026) / zoom))
    ctx.font = `${fs}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`
    ctx.fillStyle = 'rgba(255,255,255,0.45)'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('▲ ' + (lang === 'en' ? axisY.minEn : axisY.minUk), W / 2, H * 0.035)
    ctx.fillText('▼ ' + (lang === 'en' ? axisY.maxEn : axisY.maxUk), W / 2, H * 0.965)
    ctx.textAlign = 'left'
    ctx.fillText('◀ ' + (lang === 'en' ? axisX.minEn : axisX.minUk), W * 0.025, H / 2)
    ctx.textAlign = 'right'
    ctx.fillText((lang === 'en' ? axisX.maxEn : axisX.maxUk) + ' ▶', W * 0.975, H / 2)

    // Product dots
    const tx = targetVector[axisXKey] ?? 0.5
    const ty = targetVector[axisYKey] ?? 0.5
    const isAreaMode = compassMode === 'area'

    products.forEach((prod) => {
      const px = (prod.vector[axisXKey] ?? 0.5) * W
      const py = (prod.vector[axisYKey] ?? 0.5) * H
      const isRef = referencePerfume?.id === prod.id
      const isHov = hoveredProduct?.id === prod.id || selectedProduct?.id === prod.id
      const dist = Math.hypot(px - tx * W, py - ty * H) / W
      const isNear = dist < 0.14

      // Filter check
      let matchesFilter = true
      if (genderFilter !== 'all' && prod.gender && prod.gender.length > 0) {
        const gStr = prod.gender.join(' ').toLowerCase()
        if (genderFilter === 'female' && !gStr.includes('жіноч') && !gStr.includes('female')) matchesFilter = false
        if (genderFilter === 'male' && !gStr.includes('чоловіч') && !gStr.includes('мускульн') && !gStr.includes('male')) matchesFilter = false
        if (genderFilter === 'unisex' && !gStr.includes('унісекс') && !gStr.includes('unisex')) matchesFilter = false
      }
      if (seasonFilter !== 'all' && prod.seasons && prod.seasons.length > 0) {
        const sStr = prod.seasons.join(' ').toLowerCase()
        if (seasonFilter === 'warm' && !sStr.includes('літ') && !sStr.includes('весн') && !sStr.includes('summer') && !sStr.includes('spring')) matchesFilter = false
        if (seasonFilter === 'cold' && !sStr.includes('осін') && !sStr.includes('зим') && !sStr.includes('autumn') && !sStr.includes('winter')) matchesFilter = false
      }

      const baseR = isRef ? W * 0.03 : isHov ? W * 0.024 : isNear ? W * 0.015 : isAreaMode ? W * 0.0075 : W * 0.011
      const r = baseR / Math.sqrt(zoom)
      const defaultAlpha = isAreaMode ? 0.32 : 0.55
      const alpha = !matchesFilter ? 0.1 : isRef ? 1 : isHov ? 1 : isNear ? 0.95 : defaultAlpha

      if (isRef) {
        ctx.beginPath()
        ctx.arc(px, py, r * 2.2, 0, Math.PI * 2)
        ctx.strokeStyle = '#f59e0b'
        ctx.lineWidth = 2 / zoom
        ctx.stroke()
      }

      if ((isNear || isHov || isRef) && matchesFilter) {
        const glow = ctx.createRadialGradient(px, py, 0, px, py, r * 3.5)
        glow.addColorStop(0, isRef ? 'rgba(245,158,11,0.7)' : isHov ? 'rgba(255,255,255,0.5)' : 'rgba(200,169,110,0.5)')
        glow.addColorStop(1, 'transparent')
        ctx.beginPath()
        ctx.arc(px, py, r * 3.5, 0, Math.PI * 2)
        ctx.fillStyle = glow
        ctx.fill()
      }

      ctx.beginPath()
      ctx.arc(px, py, r, 0, Math.PI * 2)
      ctx.fillStyle = isRef ? '#f59e0b' : isHov ? '#ffffff' : isNear ? '#d7b171' : `rgba(255,255,255,${alpha})`
      ctx.fill()

      // When zoomed in or reference/hovered, draw miniature perfume name labels
      if ((zoom >= 1.6 || isHov || isRef) && matchesFilter) {
        ctx.font = `${Math.round(10 / Math.sqrt(zoom))}px sans-serif`
        ctx.fillStyle = isRef ? '#f59e0b' : isHov ? '#ffffff' : 'rgba(248,243,234,0.75)'
        ctx.textAlign = 'center'
        ctx.fillText((isRef ? '👑 ' : '') + prod.name_uk.split(' ')[0], px, py - r - 4 / zoom)
      }
    })

    // Target Golden Beacon (User Locator)
    const ux = tx * W
    const uy = ty * H

    // Beacon Crosshair Guide Lines when dragging
    if (isDraggingBeacon) {
      ctx.save()
      ctx.strokeStyle = 'rgba(215, 177, 113, 0.45)'
      ctx.lineWidth = 1 / zoom
      ctx.setLineDash([4 / zoom, 4 / zoom])
      ctx.beginPath()
      ctx.moveTo(0, uy)
      ctx.lineTo(W, uy)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(ux, 0)
      ctx.lineTo(ux, H)
      ctx.stroke()
      ctx.restore()
    }

    // Subtle connecting ray to nearest perfume in zone
    if (nearestProductToTarget) {
      const npx = (nearestProductToTarget.vector[axisXKey] ?? 0.5) * W
      const npy = (nearestProductToTarget.vector[axisYKey] ?? 0.5) * H
      ctx.save()
      ctx.strokeStyle = 'rgba(215, 177, 113, 0.55)'
      ctx.lineWidth = 1.2 / zoom
      ctx.setLineDash([3 / zoom, 3 / zoom])
      ctx.beginPath()
      ctx.moveTo(ux, uy)
      ctx.lineTo(npx, npy)
      ctx.stroke()
      ctx.restore()
    }

    const auraR = (W * (isDraggingBeacon ? 0.075 : 0.055)) / Math.sqrt(zoom)
    const aura = ctx.createRadialGradient(ux, uy, 0, ux, uy, auraR)
    aura.addColorStop(0, isDraggingBeacon ? 'rgba(245,158,11,0.95)' : 'rgba(200,169,110,0.95)')
    aura.addColorStop(0.5, isDraggingBeacon ? 'rgba(215,177,113,0.45)' : 'rgba(200,169,110,0.35)')
    aura.addColorStop(1, 'transparent')
    ctx.beginPath()
    ctx.arc(ux, uy, auraR, 0, Math.PI * 2)
    ctx.fillStyle = aura
    ctx.fill()

    // Inner Core
    ctx.beginPath()
    ctx.arc(ux, uy, (W * 0.015) / Math.sqrt(zoom), 0, Math.PI * 2)
    ctx.fillStyle = '#f59e0b'
    ctx.fill()

    // Ring
    const ringR = (W * 0.026) / Math.sqrt(zoom)
    ctx.beginPath()
    ctx.arc(ux, uy, ringR, 0, Math.PI * 2)
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 1.5 / Math.sqrt(zoom)
    ctx.stroke()

    // Crosshair Ticks
    const tickLen = 6 / Math.sqrt(zoom)
    ctx.strokeStyle = 'rgba(255,255,255,0.85)'
    ctx.lineWidth = 1.2 / Math.sqrt(zoom)
    ctx.beginPath()
    ctx.moveTo(ux, uy - ringR - tickLen)
    ctx.lineTo(ux, uy - ringR)
    ctx.moveTo(ux, uy + ringR)
    ctx.lineTo(ux, uy + ringR + tickLen)
    ctx.moveTo(ux - ringR - tickLen, uy)
    ctx.lineTo(ux - ringR, uy)
    ctx.moveTo(ux + ringR, uy)
    ctx.lineTo(ux + ringR + tickLen, uy)
    ctx.stroke()

    // Floating Coordinate Badge above Beacon
    const badgeText = `${Math.round(tx * 100)}% · ${Math.round(ty * 100)}%`
    ctx.font = `bold ${Math.round(9 / Math.sqrt(zoom))}px monospace`
    const badgeW = ctx.measureText(badgeText).width + 8 / Math.sqrt(zoom)
    const badgeH = 14 / Math.sqrt(zoom)
    const badgeY = uy - ringR - tickLen - 12 / Math.sqrt(zoom)
    ctx.fillStyle = 'rgba(17, 15, 24, 0.88)'
    ctx.fillRect(ux - badgeW / 2, badgeY, badgeW, badgeH)
    ctx.strokeStyle = 'rgba(200,169,110,0.6)'
    ctx.lineWidth = 1 / Math.sqrt(zoom)
    ctx.strokeRect(ux - badgeW / 2, badgeY, badgeW, badgeH)
    ctx.fillStyle = '#ebd5ab'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(badgeText, ux, badgeY + badgeH / 2)

    ctx.restore()
  }, [
    activeTab,
    projection,
    targetVector,
    hoveredProduct,
    selectedProduct,
    products,
    axisXKey,
    axisYKey,
    lang,
    axisX,
    axisY,
    zoom,
    pan,
    compassMode,
    isDraggingBeacon,
    nearestProductToTarget,
    genderFilter,
    seasonFilter,
    referencePerfume,
  ])

  // ─────────────────────────────────────────────────────────────
  // 2. RADAR DRAWING (INTERACTIVE 5D RADAR CANVAS)
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'radar' && activeTab !== 'versus') return
    const cv = radarRef.current
    if (!cv) return
    const ctx = cv.getContext('2d')
    if (!ctx) return

    const W = cv.width
    const H = cv.height
    ctx.clearRect(0, 0, W, H)
    const cx = W / 2
    const cy = H / 2
    const maxR = W * 0.36

    const axes: (keyof PerfumeVector)[] = [
      'freshness',
      'sharpness',
      'sillage',
      'sweetness',
      'formality',
    ]
    const labels = [
      lang === 'en' ? 'Freshness' : 'Свіжість',
      lang === 'en' ? 'Sharpness' : 'Різкість',
      lang === 'en' ? 'Sillage' : 'Шлейф',
      lang === 'en' ? 'Sweetness' : 'Солодкість',
      lang === 'en' ? 'Occasion' : 'Формат',
    ]
    const n = axes.length
    const step = (Math.PI * 2) / n

    // Concentric Web rings
    for (let l = 1; l <= 4; l++) {
      const r = maxR * (l / 4)
      ctx.beginPath()
      for (let i = 0; i < n; i++) {
        const ang = i * step - Math.PI / 2
        const x = cx + Math.cos(ang) * r
        const y = cy + Math.sin(ang) * r
        if (i === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      }
      ctx.closePath()
      ctx.strokeStyle = l === 4 ? 'rgba(200,169,110,0.25)' : 'rgba(255,255,255,0.07)'
      ctx.lineWidth = l === 4 ? 1.5 : 1
      ctx.stroke()
    }

    // Spokes & Labels
    ctx.font = "bold 11px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    for (let i = 0; i < n; i++) {
      const ang = i * step - Math.PI / 2
      ctx.beginPath()
      ctx.moveTo(cx, cy)
      ctx.lineTo(cx + Math.cos(ang) * maxR, cy + Math.sin(ang) * maxR)
      ctx.strokeStyle = 'rgba(255,255,255,0.12)'
      ctx.stroke()

      const lx = cx + Math.cos(ang) * (maxR + 26)
      const ly = cy + Math.sin(ang) * (maxR + 26)
      ctx.fillStyle = draggingAxis === axes[i] ? '#d7b171' : 'rgba(248,243,234,0.75)'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(labels[i], lx, ly)
    }

    // Silhouette contour of hovered product (if any)
    if (hoveredProduct) {
      ctx.beginPath()
      axes.forEach((a, i) => {
        const val = hoveredProduct.vector[a] ?? 0.5
        const r = maxR * val
        const ang = i * step - Math.PI / 2
        const x = cx + Math.cos(ang) * r
        const y = cy + Math.sin(ang) * r
        if (i === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      })
      ctx.closePath()
      ctx.setLineDash([4, 4])
      ctx.strokeStyle = '#38bdf8'
      ctx.lineWidth = 2
      ctx.stroke()
      ctx.fillStyle = 'rgba(56,189,248,0.12)'
      ctx.fill()
      ctx.setLineDash([])
    }

    // Dual Radar: Reference or Versus Product Silhouette (Neom Turquoise)
    const compareProd = versusProduct || referencePerfume
    if (compareProd) {
      ctx.beginPath()
      axes.forEach((a, i) => {
        const val = compareProd.vector[a] ?? 0.5
        const r = maxR * val
        const ang = i * step - Math.PI / 2
        const x = cx + Math.cos(ang) * r
        const y = cy + Math.sin(ang) * r
        if (i === 0) ctx.moveTo(x, y)
        else ctx.lineTo(x, y)
      })
      ctx.closePath()
      ctx.fillStyle = 'rgba(6,182,212,0.18)'
      ctx.fill()
      ctx.setLineDash([5, 5])
      ctx.strokeStyle = '#06b6d4'
      ctx.lineWidth = 2
      ctx.stroke()
      ctx.setLineDash([])

      // Glowing anchor dots for compareProd
      axes.forEach((a, i) => {
        const val = compareProd.vector[a] ?? 0.5
        const r = maxR * val
        const ang = i * step - Math.PI / 2
        const x = cx + Math.cos(ang) * r
        const y = cy + Math.sin(ang) * r
        ctx.beginPath()
        ctx.arc(x, y, 4, 0, Math.PI * 2)
        ctx.fillStyle = '#06b6d4'
        ctx.fill()
      })
    }

    // Main Target Polygon (User Shape)
    ctx.beginPath()
    axes.forEach((a, i) => {
      const val = targetVector[a] ?? 0.5
      const r = maxR * val
      const ang = i * step - Math.PI / 2
      const x = cx + Math.cos(ang) * r
      const y = cy + Math.sin(ang) * r
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    })
    ctx.closePath()
    ctx.fillStyle = 'rgba(200,169,110,0.32)'
    ctx.fill()
    ctx.strokeStyle = '#d7b171'
    ctx.lineWidth = 2.5
    ctx.stroke()

    // Draggable Interactive Vertex Handles
    axes.forEach((a, i) => {
      const val = targetVector[a] ?? 0.5
      const r = maxR * val
      const ang = i * step - Math.PI / 2
      const vx = cx + Math.cos(ang) * r
      const vy = cy + Math.sin(ang) * r
      const isDrag = draggingAxis === a

      // Glowing outer ring for easy tap/drag
      ctx.beginPath()
      ctx.arc(vx, vy, isDrag ? 14 : 10, 0, Math.PI * 2)
      ctx.fillStyle = isDrag ? 'rgba(215,177,113,0.5)' : 'rgba(215,177,113,0.2)'
      ctx.fill()
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = isDrag ? 2 : 1.5
      ctx.stroke()

      // Inner dot
      ctx.beginPath()
      ctx.arc(vx, vy, isDrag ? 6 : 4, 0, Math.PI * 2)
      ctx.fillStyle = '#ffffff'
      ctx.fill()
    })
  }, [activeTab, targetVector, draggingAxis, hoveredProduct, versusProduct, referencePerfume, lang])

  // ─────────────────────────────────────────────────────────────
  // RADAR INTERACTION (TOUCH / MOUSE DRAGGING ON RADAR)
  // ─────────────────────────────────────────────────────────────
  const handleRadarPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const cv = radarRef.current
    if (!cv) return
    cv.setPointerCapture(e.pointerId)

    const rect = cv.getBoundingClientRect()
    const scaleX = cv.width / rect.width
    const scaleY = cv.height / rect.height
    const px = (e.clientX - rect.left) * scaleX
    const py = (e.clientY - rect.top) * scaleY

    const cx = cv.width / 2
    const cy = cv.height / 2
    const maxR = cv.width * 0.36
    const axes: (keyof PerfumeVector)[] = [
      'freshness',
      'sharpness',
      'sillage',
      'sweetness',
      'formality',
    ]
    const step = (Math.PI * 2) / axes.length

    // Check which vertex was tapped/clicked
    let closestAxis: keyof PerfumeVector | null = null
    let minDist = 36 // hit tolerance in canvas px

    axes.forEach((a, i) => {
      const val = targetVector[a] ?? 0.5
      const r = maxR * val
      const ang = i * step - Math.PI / 2
      const vx = cx + Math.cos(ang) * r
      const vy = cy + Math.sin(ang) * r
      const d = Math.hypot(px - vx, py - vy)
      if (d < minDist) {
        minDist = d
        closestAxis = a
      }
    })

    // If tapped near an axis spoke, select that axis
    if (!closestAxis) {
      const clickAng = Math.atan2(py - cy, px - cx) + Math.PI / 2
      const normAng = (clickAng + Math.PI * 2) % (Math.PI * 2)
      const closestIdx = Math.round(normAng / step) % axes.length
      closestAxis = axes[closestIdx]
    }

    if (closestAxis) {
      setDraggingAxis(closestAxis)
      haptic('light')
      updateRadarAxisFromPos(closestAxis, px, py, cx, cy, maxR)
    }
  }

  const handleRadarPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!draggingAxis) return
    const cv = radarRef.current
    if (!cv) return

    const rect = cv.getBoundingClientRect()
    const scaleX = cv.width / rect.width
    const scaleY = cv.height / rect.height
    const px = (e.clientX - rect.left) * scaleX
    const py = (e.clientY - rect.top) * scaleY

    const cx = cv.width / 2
    const cy = cv.height / 2
    const maxR = cv.width * 0.36

    updateRadarAxisFromPos(draggingAxis, px, py, cx, cy, maxR)
  }

  const handleRadarPointerUp = () => {
    setDraggingAxis(null)
  }

  const updateRadarAxisFromPos = (
    axis: keyof PerfumeVector,
    px: number,
    py: number,
    cx: number,
    cy: number,
    maxR: number,
  ) => {
    const distFromCenter = Math.hypot(px - cx, py - cy)
    const normalized = Math.max(0.08, Math.min(0.96, distFromCenter / maxR))
    setTargetVector((prev) => ({
      ...prev,
      [axis]: Math.round(normalized * 100) / 100,
    }))
  }

  // ─────────────────────────────────────────────────────────────
  // COMPASS MAP INTERACTION (PAN & ZOOM)
  // ─────────────────────────────────────────────────────────────
  const handleMapPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const cv = canvasRef.current
    if (!cv) return
    cv.setPointerCapture(e.pointerId)

    const rect = cv.getBoundingClientRect()
    const clientX = e.clientX - rect.left
    const clientY = e.clientY - rect.top

    // Convert click position to unzoomed normalized map coords
    const W = cv.width
    const H = cv.height
    const scaleX = W / rect.width
    const scaleY = H / rect.height
    const canvasX = clientX * scaleX
    const canvasY = clientY * scaleY

    // Un-transform coordinates
    const unscaledX = (canvasX - (W / 2 + pan.x)) / zoom + W / 2
    const unscaledY = (canvasY - (H / 2 + pan.y)) / zoom + H / 2
    const normX = Math.max(0.04, Math.min(0.96, unscaledX / W))
    const normY = Math.max(0.04, Math.min(0.96, unscaledY / H))

    // In 'inspect' mode: inspect individual perfume dots or pan
    if (compassMode === 'inspect') {
      const clickedProd = products.find((p) => {
        const px = (p.vector[axisXKey] ?? 0.5) * W
        const py = (p.vector[axisYKey] ?? 0.5) * H
        return Math.hypot(px - unscaledX, py - unscaledY) < 22 / Math.sqrt(zoom)
      })

      if (clickedProd) {
        setSelectedProduct(clickedProd)
        setHoveredProduct(clickedProd)
        haptic('medium')
        return
      }

      // Empty space click in inspect mode starts canvas panning
      setIsPanning(true)
      dragStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y }
      return
    }

    // In 'area' mode (Area-First interaction):
    // Clicking or dragging anywhere places and moves the Golden Beacon
    setIsDraggingBeacon(true)
    const newX = Math.round(normX * 100) / 100
    const newY = Math.round(normY * 100) / 100
    lastHapticQuadrantRef.current = `${newX < 0.5 ? 'L' : 'R'}-${newY < 0.5 ? 'T' : 'B'}`
    setTargetVector((prev) => ({
      ...prev,
      [axisXKey]: newX,
      [axisYKey]: newY,
    }))
    haptic('light')
  }

  const handleMapPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const cv = canvasRef.current
    if (!cv) return

    const rect = cv.getBoundingClientRect()
    const clientX = e.clientX - rect.left
    const clientY = e.clientY - rect.top
    const W = cv.width
    const H = cv.height
    const scaleX = W / rect.width
    const scaleY = H / rect.height
    const canvasX = clientX * scaleX
    const canvasY = clientY * scaleY
    const unscaledX = (canvasX - (W / 2 + pan.x)) / zoom + W / 2
    const unscaledY = (canvasY - (H / 2 + pan.y)) / zoom + H / 2

    // 1. Dragging Golden Beacon in Area-First mode
    if (isDraggingBeacon) {
      const normX = Math.max(0.04, Math.min(0.96, unscaledX / W))
      const normY = Math.max(0.04, Math.min(0.96, unscaledY / H))
      const newX = Math.round(normX * 100) / 100
      const newY = Math.round(normY * 100) / 100

      const qTag = `${newX < 0.5 ? 'L' : 'R'}-${newY < 0.5 ? 'T' : 'B'}`
      if (lastHapticQuadrantRef.current !== qTag) {
        lastHapticQuadrantRef.current = qTag
        haptic('light')
      }

      setTargetVector((prev) => ({
        ...prev,
        [axisXKey]: newX,
        [axisYKey]: newY,
      }))
      return
    }

    // 2. Panning canvas
    if (isPanning) {
      const dx = e.clientX - dragStartRef.current.x
      const dy = e.clientY - dragStartRef.current.y
      setPan({
        x: dragStartRef.current.panX + dx,
        y: dragStartRef.current.panY + dy,
      })
      return
    }

    // 3. Hover detection for tooltip in inspect mode
    if (compassMode === 'inspect') {
      const hov = products.find((p) => {
        const px = (p.vector[axisXKey] ?? 0.5) * W
        const py = (p.vector[axisYKey] ?? 0.5) * H
        return Math.hypot(px - unscaledX, py - unscaledY) < 18 / Math.sqrt(zoom)
      })
      setHoveredProduct(hov || null)
    }
  }

  const handleMapPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      // ignore
    }
    setIsPanning(false)
    if (isDraggingBeacon) {
      setIsDraggingBeacon(false)
      haptic('light')
    }
  }

  const handleMapWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    const zoomFactor = e.deltaY < 0 ? 1.15 : 0.88
    setZoom((prev) => Math.max(0.8, Math.min(4.0, prev * zoomFactor)))
  }

  const handleSliderChange = (axis: keyof PerfumeVector, value: number) => {
    setTargetVector((prev) => ({
      ...prev,
      [axis]: value / 100,
    }))
  }

  return (
    <div className="min-h-screen bg-[#110f18] text-[#f8f3ea] px-4 py-6 md:px-8 pb-32">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <header className="text-center space-y-2 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#c8a96e]/15 border border-[#c8a96e]/30 text-xs font-semibold uppercase tracking-widest text-[#d7b171]">
            <Sparkles className="w-3.5 h-3.5" />
            <span>
              {activeTab === 'capsule'
                ? (lang === 'en' ? 'Scent Capsule Wardrobe' : lang === 'ru' ? 'Конструктор гардероба' : 'Конструктор парфумерного гардеробу')
                : activeTab === 'versus'
                ? (lang === 'en' ? 'Olfactory Battle' : lang === 'ru' ? 'Ольфакторный баттл' : 'Ольфакторний батл')
                : activeTab === 'pyramid'
                ? (lang === 'en' ? 'Time Pyramid' : lang === 'ru' ? 'Пирамида во времени' : 'Піраміда в часі')
                : (lang === 'en' ? 'Multi-Axis Fragrance Discovery' : 'Сенсорна матриця ароматів')}
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold font-serif tracking-tight text-white">
            {activeTab === 'capsule'
              ? (lang === 'en' ? 'Fragrance Wardrobe 4×5ml' : lang === 'ru' ? 'Парфюмерный Гардероб 4×5 мл' : 'Парфумерний Гардероб 4×5 мл')
              : activeTab === 'versus'
              ? (lang === 'en' ? 'Scent Versus Battle' : lang === 'ru' ? 'Баттл ароматов' : 'Батл ароматів')
              : activeTab === 'pyramid'
              ? (lang === 'en' ? 'Scent Evolution in Time' : lang === 'ru' ? 'Пирамида нот в динамике' : 'Еволюція аромату в часі')
              : (lang === 'en' ? 'Scent Matrix' : 'Компас Ароматів')}
          </h1>
          <p className="text-xs md:text-sm text-neutral-400 leading-relaxed">
            {activeTab === 'capsule'
              ? (lang === 'en'
                  ? 'Build your personal 4-fragrance capsule wardrobe with balanced sensory diversity and order a custom Discovery Box with -15% discount.'
                  : lang === 'ru'
                  ? 'Соберите личный гардероб из 4 ароматов для разных ситуаций и закажите готовый Discovery Box 4×5 мл со скидкой -15%.'
                  : 'Створіть власний гардероб з 4 некорелюючих ароматів для різних ситуацій та замовте готовий Discovery Box 4×5 мл зі знижкою -15%.')
              : activeTab === 'versus'
              ? (lang === 'en'
                  ? 'Compare two fragrances simultaneously on the 5D radar: detect shared and unique olfactory notes.'
                  : lang === 'ru'
                  ? 'Сравните два аромата на 5D радаре: найдите общие и уникальные ноты пирамиды.'
                  : 'Порівняйте два аромати одночасно на 5D радарі та дізнайтеся спільні й унікальні ноти піраміди.')
              : activeTab === 'pyramid'
              ? (lang === 'en'
                  ? 'Inspect how top, heart and base notes evolve over 8 hours.'
                  : lang === 'ru'
                  ? 'Исследуйте, как раскрываются верхние, средние и базовые ноты на протяжении 8 часов.'
                  : 'Досліджуйте як розкриваються верхні, серцеві та базові ноти протягом 8 годин.')
              : (lang === 'en'
                  ? 'Multi-dimensional discovery: intersect scales of freshness, sharpness, sillage, sweetness and format in real-time.'
                  : 'Багатовимірний підбір: перетинай шкали свіжості, різкості, шлейфу, солодкості та формату в реальному часі.')}
          </p>
        </header>

        {/* Navigation Tabs (Connoisseur Edition) */}
        <div className="flex justify-center">
          <div className="bg-white/[0.04] p-1 rounded-2xl border border-white/10 flex items-center gap-1 w-full max-w-xl shadow-lg backdrop-blur-md overflow-x-auto scrollbar-none">
            <button
              type="button"
              onClick={() => handleTabChange('compass')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition ${
                activeTab === 'compass'
                  ? 'bg-[#c8a96e] text-[#110f18] shadow-md'
                  : 'text-neutral-400 hover:text-white'
              }`}
            >
              <Compass className="w-3.5 h-3.5 shrink-0" />
              <span>{lang === 'en' ? '2D Compass' : '2D Компас'}</span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('capsule')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition ${
                activeTab === 'capsule'
                  ? 'bg-[#c8a96e] text-[#110f18] shadow-md'
                  : 'text-neutral-400 hover:text-white'
              }`}
            >
              <Briefcase className="w-3.5 h-3.5 shrink-0" />
              <span>{lang === 'en' ? 'Capsule 4×5ml' : lang === 'ru' ? 'Гардероб 4×5мл' : 'Гардероб 4×5 мл'}</span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('radar')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition ${
                activeTab === 'radar'
                  ? 'bg-[#c8a96e] text-[#110f18] shadow-md'
                  : 'text-neutral-400 hover:text-white'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5 shrink-0" />
              <span>{lang === 'en' ? '5D Radar' : '5D Радар'}</span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('versus')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition ${
                activeTab === 'versus'
                  ? 'bg-[#c8a96e] text-[#110f18] shadow-md'
                  : 'text-neutral-400 hover:text-white'
              }`}
            >
              <Swords className="w-3.5 h-3.5 shrink-0" />
              <span>{lang === 'en' ? 'Battle Versus' : 'Side-by-Side'}</span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('pyramid')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition ${
                activeTab === 'pyramid'
                  ? 'bg-[#c8a96e] text-[#110f18] shadow-md'
                  : 'text-neutral-400 hover:text-white'
              }`}
            >
              <Clock className="w-3.5 h-3.5 shrink-0" />
              <span>{lang === 'en' ? 'Time Pyramid' : 'Піраміда в часі'}</span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('matrix')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition ${
                activeTab === 'matrix'
                  ? 'bg-[#c8a96e] text-[#110f18] shadow-md'
                  : 'text-neutral-400 hover:text-white'
              }`}
            >
              <Sliders className="w-3.5 h-3.5 shrink-0" />
              <span>{lang === 'en' ? 'Sliders' : 'Шкали'}</span>
            </button>
          </div>
        </div>

        {isDiscoveryTab && (
          <>
        {/* Search Reference Fragrance Bar */}
        <div className="relative">
          <div className="relative flex items-center bg-[#181622] border border-white/10 rounded-2xl px-3.5 py-2.5 shadow-lg focus-within:border-[#c8a96e]">
            <Search className="w-4 h-4 text-[#c8a96e] shrink-0 mr-2.5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              placeholder={
                lang === 'en'
                  ? 'Find similar to a perfume you love (e.g. Baccarat, Aventus, Black Gemstone)...'
                  : 'Шукаєте схожий на улюблений парфум? Введіть назву або бренд...'
              }
              className="w-full bg-transparent text-xs text-white placeholder-neutral-500 focus:outline-none"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="p-1 text-neutral-400 hover:text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Autocomplete Suggestions Popover */}
          {isSearchFocused && searchSuggestions.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-1.5 z-40 bg-[#1e1b2e] border border-[#c8a96e]/40 rounded-2xl shadow-2xl overflow-hidden divide-y divide-white/5">
              {searchSuggestions.map((sug) => (
                <button
                  key={sug.id}
                  type="button"
                  onMouseDown={() => handleSelectReference(sug)}
                  className="w-full p-2.5 flex items-center gap-3 text-left hover:bg-white/[0.06] transition"
                >
                  <div className="w-10 h-10 rounded-lg bg-black/40 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                    {sug.photo_url || sug.photo_source_url ? (
                      <img
                        src={sug.photo_url || sug.photo_source_url || ''}
                        alt={sug.name_uk}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Package className="w-4 h-4 text-neutral-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-white truncate">
                      {localizedName(lang, sug.name_uk, sug.name_en)}
                    </p>
                    {sug.brand && (
                      <p className="text-[10px] text-[#c8a96e] truncate">{sug.brand}</p>
                    )}
                  </div>
                  <span className="text-[10px] font-semibold text-[#c8a96e] border border-[#c8a96e]/40 rounded-full px-2 py-0.5">
                    {lang === 'en' ? 'Select as anchor' : 'Обрати еталоном'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Active Reference Perfume Banner */}
        {referencePerfume && (
          <div className="flex items-center justify-between bg-[#c8a96e]/15 border border-[#c8a96e]/40 rounded-2xl px-4 py-2.5 text-xs text-[#ebd5ab]">
            <div className="flex items-center gap-2 truncate">
              <Sparkles className="w-4 h-4 text-[#c8a96e] shrink-0" />
              <span className="truncate">
                {lang === 'en' ? 'Reference anchor: ' : 'Еталон пошуку: '}
                <strong className="text-white font-bold">
                  {localizedName(lang, referencePerfume.name_uk, referencePerfume.name_en)}
                </strong>
                {referencePerfume.brand ? ` (${referencePerfume.brand})` : ''}
              </span>
            </div>
            <button
              type="button"
              onClick={handleClearReference}
              className="text-[11px] font-bold text-neutral-400 hover:text-white flex items-center gap-1 shrink-0 ml-2"
            >
              <X className="w-3.5 h-3.5" />
              <span>{lang === 'en' ? 'Clear' : 'Скинути'}</span>
            </button>
          </div>
        )}

        {/* Filter Chips Bar (Gender & Season) */}
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none py-1">
            <span className="text-[11px] text-neutral-400 font-medium mr-1 flex items-center gap-1">
              <Filter className="w-3 h-3 text-[#c8a96e]" />
              <span>{lang === 'en' ? 'Gender:' : 'Стать:'}</span>
            </span>
            {[
              { id: 'all', label: lang === 'en' ? 'All' : 'Всі' },
              { id: 'female', label: lang === 'en' ? '🌸 Women' : '🌸 Жіночі' },
              { id: 'male', label: lang === 'en' ? '🪵 Men' : '🪵 Чоловічі' },
              { id: 'unisex', label: lang === 'en' ? '✨ Unisex' : '✨ Унісекс' },
            ].map((btn) => (
              <button
                key={btn.id}
                type="button"
                onClick={() => setGenderFilter(btn.id as any)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition ${
                  genderFilter === btn.id
                    ? 'bg-[#c8a96e] text-[#110f18] font-bold shadow'
                    : 'bg-white/[0.04] text-neutral-300 hover:bg-white/[0.08]'
                }`}
              >
                {btn.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none py-1">
            <span className="text-[11px] text-neutral-400 font-medium mr-1">
              {lang === 'en' ? 'Season:' : 'Сезон:'}
            </span>
            {[
              { id: 'all', label: lang === 'en' ? 'All' : 'Всі' },
              { id: 'warm', label: lang === 'en' ? '☀️ Warm' : '☀️ Теплий' },
              { id: 'cold', label: lang === 'en' ? '❄️ Cold' : '❄️ Холодний' },
            ].map((btn) => (
              <button
                key={btn.id}
                type="button"
                onClick={() => setSeasonFilter(btn.id as any)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition ${
                  seasonFilter === btn.id
                    ? 'bg-[#c8a96e] text-[#110f18] font-bold shadow'
                    : 'bg-white/[0.04] text-neutral-300 hover:bg-white/[0.08]'
                }`}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>

        {/* Connoisseur Note Synergy Multi-Select Filter Bar */}
        <div className="space-y-1.5 bg-white/[0.02] border border-white/5 rounded-2xl p-2.5">
          <div className="flex items-center justify-between text-xs px-1">
            <span className="font-semibold text-xs text-[#d7b171] flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-[#d7b171]" />
              <span>{lang === 'en' ? 'Olfactory Note Filter' : 'Нішеві ноти & інгредієнти'}</span>
            </span>
            {selectedNotes.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedNotes([])}
                className="text-[10px] text-pink-400 hover:text-pink-300 font-semibold flex items-center gap-0.5"
              >
                <X className="w-3 h-3" />
                <span>{lang === 'en' ? 'Reset notes' : 'Очистити ноти'}</span>
              </button>
            )}
          </div>
          <div className="flex gap-1.5 overflow-x-auto scrollbar-none py-0.5">
            {[
              { id: 'ладан', labelUk: '🕯️ Ладан', labelEn: '🕯️ Incense' },
              { id: 'ветивер', labelUk: '🌾 Ветивер', labelEn: '🌾 Vetiver' },
              { id: 'шкір', labelUk: '👞 Шкіра / Замша', labelEn: '👞 Leather' },
              { id: 'уд', labelUk: '🪵 Уд', labelEn: '🪵 Oud' },
              { id: 'ірис', labelUk: '💜 Ірис / Пудра', labelEn: '💜 Iris / Powder' },
              { id: 'амбр', labelUk: '✨ Амбра', labelEn: '✨ Amber' },
              { id: 'тютюн', labelUk: '🍂 Тютюн', labelEn: '🍂 Tobacco' },
              { id: 'ваніл', labelUk: '🍦 Ваніль', labelEn: '🍦 Vanilla' },
              { id: 'бергамот', labelUk: '🍋 Бергамот', labelEn: '🍋 Bergamot' },
              { id: 'кедр', labelUk: '🌲 Кедр', labelEn: '🌲 Cedar' },
            ].map((n) => {
              const isSelected = selectedNotes.includes(n.id)
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => {
                    haptic('light')
                    setSelectedNotes((prev) =>
                      prev.includes(n.id) ? prev.filter((x) => x !== n.id) : [...prev, n.id],
                    )
                  }}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition border ${
                    isSelected
                      ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50 shadow'
                      : 'bg-white/[0.03] text-neutral-300 border-white/10 hover:border-white/20'
                  }`}
                >
                  {lang === 'en' ? n.labelEn : n.labelUk}
                </button>
              )
            })}
          </div>
        </div>

        {/* Archetypes Palette Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-neutral-400 px-1">
            <span className="font-semibold uppercase tracking-wider text-[#d7b171]">
              {lang === 'en' ? 'Archetype Presets' : 'Готові архетипи'}
            </span>
            <span className="text-[11px] opacity-70">
              {lang === 'en' ? '1-click sensory target' : 'В один клік'}
            </span>
          </div>
          <div className="flex gap-2.5 overflow-x-auto pb-1.5 scrollbar-none sm:grid sm:grid-cols-5">
            {ARCHETYPES.map((arch) => {
              const Icon = arch.icon
              const isMatch = calculateGaussianMatch(arch.vector, targetVector) >= 90
              return (
                <button
                  key={arch.id}
                  type="button"
                  onClick={() => {
                    haptic('medium')
                    setTargetVector(arch.vector)
                  }}
                  className={`flex flex-col items-start p-3 rounded-2xl border text-left transition-all min-w-[145px] shrink-0 sm:min-w-0 ${
                    isMatch
                      ? 'bg-[#c8a96e]/20 border-[#c8a96e] shadow-lg shadow-[#c8a96e]/10 scale-[1.02]'
                      : 'bg-white/[0.03] border-white/10 hover:border-white/20 hover:bg-white/[0.05]'
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1 w-full">
                    <Icon className="w-4 h-4 text-[#d7b171] shrink-0" />
                    <span className="text-xs font-bold text-white truncate">
                      {lang === 'en' ? arch.nameEn : arch.nameUk}
                    </span>
                  </div>
                  <span className="text-[10px] text-neutral-400 line-clamp-1 leading-tight">
                    {lang === 'en' ? arch.descEn : arch.descUk}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
        </>
        )}

        {/* 1. Main Interactive Visual Tool (Full Width) */}
        <div className="w-full space-y-4">
          {/* ─── TAB 1: 2D COMPASS MAP WITH ZOOM & PAN ─── */}
            {activeTab === 'compass' && (
              <div className="space-y-3">
                {/* Projection Selector */}
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
                  {[
                    {
                      id: 'freshness-sillage',
                      labelUk: '❄️ Свіжість ✕ 💨 Шлейф',
                      labelEn: '❄️ Freshness ✕ 💨 Sillage',
                      labelRu: '❄️ Свежесть ✕ 💨 Шлейф',
                    },
                    {
                      id: 'freshness-sharpness',
                      labelUk: '❄️ Температура ✕ 🌶️ Різкість',
                      labelEn: '❄️ Temperature ✕ 🌶️ Sharpness',
                      labelRu: '❄️ Температура ✕ 🌶️ Резкость',
                    },
                    {
                      id: 'sweetness-formality',
                      labelUk: '🍯 Солодкість ✕ 👑 Формат',
                      labelEn: '🍯 Sweetness ✕ 👑 Occasion',
                      labelRu: '🍯 Сладость ✕ 👑 Формат',
                    },
                    {
                      id: 'sharpness-sillage',
                      labelUk: '🗡️ Різкість ✕ 💨 Шлейф',
                      labelEn: '🗡️ Sharpness ✕ 💨 Sillage',
                      labelRu: '🗡️ Резкость ✕ 💨 Шлейф',
                    },
                  ].map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        haptic('light')
                        setProjection(p.id as ProjectionKey)
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition border ${
                        projection === p.id
                          ? 'bg-[#c8a96e]/20 text-[#ebd5ab] border-[#c8a96e]'
                          : 'bg-white/[0.03] text-neutral-400 border-white/10 hover:border-white/20'
                      }`}
                    >
                      {lang === 'en' ? p.labelEn : lang === 'ru' ? p.labelRu : p.labelUk}
                    </button>
                  ))}
                </div>

                {/* 4 Quick Quadrant Presets */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs px-1">
                    <span className="font-semibold uppercase tracking-wider text-[#d7b171] text-[11px] flex items-center gap-1.5">
                      <Target className="w-3.5 h-3.5" />
                      <span>
                        {lang === 'en'
                          ? 'Sensory Zones (4 Quadrants)'
                          : lang === 'ru'
                            ? 'Сенсорные зоны (4 квадранта)'
                            : 'Сенсорні зони (4 квадранти)'}
                      </span>
                    </span>
                    <span className="text-[11px] text-neutral-400">
                      {lang === 'en' ? 'Tap to move beacon' : lang === 'ru' ? 'Клик для маяка' : 'Клік для маяка'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {quadrantPresets.map((quad) => {
                      const isActive = activeQuadrant.id === quad.id
                      const name = lang === 'en' ? quad.nameEn : lang === 'ru' ? quad.nameRu : quad.nameUk
                      const desc = lang === 'en' ? quad.descEn : lang === 'ru' ? quad.descRu : quad.descUk
                      return (
                        <button
                          key={quad.id}
                          type="button"
                          onClick={() => {
                            haptic('medium')
                            setTargetVector((prev) => ({
                              ...prev,
                              [axisXKey]: quad.x,
                              [axisYKey]: quad.y,
                            }))
                          }}
                          className={`p-2.5 rounded-xl border text-left transition-all flex flex-col justify-between ${
                            isActive
                              ? 'bg-[#c8a96e]/20 border-[#c8a96e] shadow-md shadow-[#c8a96e]/15 scale-[1.02]'
                              : 'bg-white/[0.03] border-white/10 hover:border-white/20 hover:bg-white/[0.06]'
                          }`}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-base shrink-0">{quad.icon}</span>
                            <span className="text-xs font-bold text-white truncate">{name}</span>
                          </div>
                          <p className="text-[10px] text-neutral-400 line-clamp-1 leading-snug">{desc}</p>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Canvas Container with Map Zoom Controls & HUD */}
                <div className="relative bg-[#181622] border border-white/10 rounded-2xl overflow-hidden shadow-2xl p-3">
                  {/* Header: Axis Title & Mode Switcher */}
                  <div className="flex flex-wrap justify-between items-center gap-2 mb-2 px-1 text-xs">
                    <span className="font-semibold text-[#d7b171] uppercase tracking-wider">
                      {axisX.nameUk} ✕ {axisY.nameUk}
                    </span>
                    <div className="flex items-center gap-1 bg-white/[0.06] p-0.5 rounded-lg border border-white/10">
                      <button
                        type="button"
                        onClick={() => {
                          setCompassMode('area')
                          haptic('light')
                        }}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition ${
                          compassMode === 'area'
                            ? 'bg-[#c8a96e] text-[#110f18] shadow'
                            : 'text-neutral-400 hover:text-white'
                        }`}
                      >
                        <Target className="w-3 h-3" />
                        <span>{lang === 'en' ? 'Zone & Beacon' : lang === 'ru' ? 'Зона & Маяк' : 'Зони & Маяк'}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setCompassMode('inspect')
                          haptic('light')
                        }}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition ${
                          compassMode === 'inspect'
                            ? 'bg-[#c8a96e] text-[#110f18] shadow'
                            : 'text-neutral-400 hover:text-white'
                        }`}
                      >
                        <MousePointer className="w-3 h-3" />
                        <span>{lang === 'en' ? 'Dots' : lang === 'ru' ? 'Точки' : 'Точки флаконів'}</span>
                      </button>
                    </div>
                  </div>

                  {/* Dynamic Olfactory Zone HUD Bar */}
                  <div className="mb-2.5 p-2.5 rounded-xl bg-gradient-to-r from-[#c8a96e]/15 via-white/[0.04] to-transparent border border-[#c8a96e]/30 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xl shrink-0 p-1 bg-white/[0.05] rounded-lg border border-white/10">
                        {activeQuadrant.icon}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-bold text-white truncate">
                            {lang === 'en'
                              ? activeQuadrant.nameEn
                              : lang === 'ru'
                                ? activeQuadrant.nameRu
                                : activeQuadrant.nameUk}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#c8a96e]/20 text-[#ebd5ab] border border-[#c8a96e]/30 font-medium shrink-0">
                            {zoneMatchCount}{' '}
                            {lang === 'en'
                              ? 'in zone'
                              : lang === 'ru'
                                ? 'в зоне'
                                : 'у зоні'}
                          </span>
                        </div>
                        <p className="text-[10px] text-neutral-400 truncate">
                          {lang === 'en'
                            ? activeQuadrant.descEn
                            : lang === 'ru'
                              ? activeQuadrant.descRu
                              : activeQuadrant.descUk}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 text-[11px] font-mono text-[#d7b171]">
                      <span className="bg-[#110f18]/80 px-2 py-0.5 rounded border border-[#c8a96e]/20">
                        X: {Math.round((targetVector[axisXKey] ?? 0.5) * 100)}% · Y: {Math.round((targetVector[axisYKey] ?? 0.5) * 100)}%
                      </span>
                    </div>
                  </div>

                  {/* Interactive Canvas */}
                  <div className="relative aspect-square w-full rounded-xl overflow-hidden bg-[#110f18] cursor-crosshair">
                    <canvas
                      ref={canvasRef}
                      width={500}
                      height={500}
                      className="w-full h-full touch-none select-none"
                      onPointerDown={handleMapPointerDown}
                      onPointerMove={handleMapPointerMove}
                      onPointerUp={handleMapPointerUp}
                      onWheel={handleMapWheel}
                    />

                    {/* Map Zoom / Navigation Overlay Controls */}
                    <div className="absolute right-3 top-3 flex flex-col gap-1.5 bg-[#181622]/90 border border-white/10 rounded-xl p-1 backdrop-blur-md shadow-xl">
                      <button
                        type="button"
                        aria-label="Zoom in"
                        onClick={() => setZoom((z) => Math.min(4.0, z + 0.3))}
                        className="p-2 rounded-lg hover:bg-white/10 text-[#d7b171] transition active:scale-95"
                      >
                        <ZoomIn className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        aria-label="Zoom out"
                        onClick={() => setZoom((z) => Math.max(0.8, z - 0.3))}
                        className="p-2 rounded-lg hover:bg-white/10 text-neutral-300 transition active:scale-95"
                      >
                        <ZoomOut className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        aria-label="Reset view"
                        onClick={handleResetZoom}
                        className="p-2 rounded-lg hover:bg-white/10 text-neutral-400 transition active:scale-95"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Interaction Hint Badge */}
                    <div className="absolute left-3 bottom-3 px-2.5 py-1 rounded-lg bg-[#181622]/90 border border-white/10 text-[11px] text-[#ebd5ab] backdrop-blur-md shadow-lg pointer-events-none flex items-center gap-1.5">
                      {compassMode === 'area' ? (
                        <>
                          <Crosshair className="w-3.5 h-3.5 text-[#d7b171] animate-pulse" />
                          <span>
                            {lang === 'en'
                              ? 'Tap or drag beacon to explore olfactory zone'
                              : lang === 'ru'
                                ? 'Нажмите или ведите маяк для исследования'
                                : 'Торкніться або ведіть маяк для дослідження зони'}
                          </span>
                        </>
                      ) : (
                        <>
                          <MousePointer className="w-3.5 h-3.5 text-[#d7b171]" />
                          <span>
                            {lang === 'en'
                              ? 'Tap perfume dots to inspect details'
                              : lang === 'ru'
                                ? 'Нажимайте на точки флаконов для деталей'
                                : 'Торкніться точок флаконів для перегляду'}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Zoom Level Indicator */}
                    {zoom !== 1.0 && (
                      <div className="absolute right-3 bottom-3 px-2 py-0.5 rounded-md bg-[#181622]/80 border border-white/10 text-[10px] font-mono text-[#d7b171] backdrop-blur-sm">
                        {Math.round(zoom * 100)}% ZOOM
                      </div>
                    )}
                  </div>

                  {/* Selected Product Popover Card or Nearest Fragrance in Zone Card */}
                  {selectedProduct ? (
                    <div className="mt-3 p-3 rounded-xl bg-white/[0.05] border border-[#c8a96e]/40 flex items-center justify-between gap-3 animate-in fade-in duration-200">
                      <div className="flex items-center gap-3 min-w-0">
                        {selectedProduct.photo_url || selectedProduct.photo_source_url ? (
                          <img
                            src={selectedProduct.photo_url || selectedProduct.photo_source_url || ''}
                            alt=""
                            className="w-10 h-10 rounded-lg object-cover bg-neutral-900 shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-neutral-900 flex items-center justify-center shrink-0">
                            <Package className="w-5 h-5 text-neutral-500" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-white truncate">
                            {selectedProduct.name_uk}
                          </p>
                          <p className="text-[11px] text-[#c9a24b] font-bold">
                            {formatPrice(
                              selectedProduct.price_uah ||
                                (selectedProduct.price_usd ? Math.round(selectedProduct.price_usd * 44) : 0),
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => setSelectedProduct(null)}
                          className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-white/10"
                          aria-label="Clear selection"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <Link
                          to="/catalog/$productSlug"
                          params={{ productSlug: selectedProduct.slug || String(selectedProduct.id) }}
                          className="px-3 py-1.5 rounded-lg bg-[#c8a96e] text-[#110f18] text-xs font-bold flex items-center gap-1 shrink-0 hover:bg-[#d4af37]"
                        >
                          <span>{lang === 'en' ? 'Open' : 'Відкрити'}</span>
                          <ChevronRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  ) : nearestProductToTarget ? (
                    <div className="mt-3 p-3 rounded-xl bg-white/[0.03] border border-white/10 flex items-center justify-between gap-3 animate-in fade-in duration-200">
                      <div className="flex items-center gap-3 min-w-0">
                        {nearestProductToTarget.photo_url || nearestProductToTarget.photo_source_url ? (
                          <img
                            src={nearestProductToTarget.photo_url || nearestProductToTarget.photo_source_url || ''}
                            alt=""
                            className="w-10 h-10 rounded-lg object-cover bg-neutral-900 shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-neutral-900 flex items-center justify-center shrink-0">
                            <Package className="w-5 h-5 text-neutral-500" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-semibold text-[#d7b171] uppercase tracking-wider flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              <span>{lang === 'en' ? 'Closest fragrance' : lang === 'ru' ? 'Ближайший аромат' : 'Найближчий аромат'}</span>
                            </span>
                          </div>
                          <p className="text-xs font-bold text-white truncate">
                            {nearestProductToTarget.name_uk}
                          </p>
                          <p className="text-[11px] text-[#c9a24b] font-bold">
                            {formatPrice(
                              nearestProductToTarget.price_uah ||
                                (nearestProductToTarget.price_usd ? Math.round(nearestProductToTarget.price_usd * 44) : 0),
                            )}
                          </p>
                        </div>
                      </div>
                      <Link
                        to="/catalog/$productSlug"
                        params={{ productSlug: nearestProductToTarget.slug || String(nearestProductToTarget.id) }}
                        className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-[#c8a96e] hover:text-[#110f18] text-[#ebd5ab] text-xs font-bold flex items-center gap-1 shrink-0 transition"
                      >
                        <span>{lang === 'en' ? 'Details' : 'Деталі'}</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {/* ─── TAB 2: 5D EQUALIZER ─── */}
            {activeTab === 'matrix' && (
              <div className="space-y-4">
                {(
                  ['freshness', 'sharpness', 'sillage', 'sweetness', 'formality'] as (keyof PerfumeVector)[]
                ).map((key) => {
                  const meta = AXIS_MAP[key]
                  const val = Math.round((targetVector[key] ?? 0.5) * 100)
                  return (
                    <div
                      key={key}
                      className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 md:p-5 space-y-2.5 shadow-lg"
                    >
                      <div className="flex justify-between items-center text-sm font-medium">
                        <span className="text-white font-semibold">
                          {lang === 'en' ? meta.nameEn : meta.nameUk}
                        </span>
                        <span className="text-[#d7b171] bg-[#c8a96e]/20 px-3 py-0.5 rounded-full border border-[#c8a96e]/30 font-bold text-xs font-mono">
                          {val}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={val}
                        onChange={(e) => handleSliderChange(key, Number(e.target.value))}
                        className="w-full accent-[#c8a96e] cursor-pointer h-2 bg-neutral-800 rounded-lg"
                      />
                      <div className="flex justify-between text-[11px] text-neutral-400">
                        <span>{lang === 'en' ? meta.minEn : meta.minUk}</span>
                        <span>{lang === 'en' ? meta.maxEn : meta.maxUk}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* ─── TAB 3: INTERACTIVE 5D RADAR ─── */}
            {activeTab === 'radar' && (
              <div className="bg-[#181622] border border-white/10 rounded-2xl p-6 flex flex-col items-center justify-center space-y-4 shadow-2xl">
                <div className="text-center space-y-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#d7b171]">
                    {lang === 'en' ? 'Sculpt your Scent Shape' : 'Скульптор аромату'}
                  </span>
                  <p className="text-[11px] text-neutral-400">
                    {lang === 'en'
                      ? 'Touch and drag vertices on the radar to sculpt your custom fragrance geometry'
                      : 'Торкніться та перетягуйте вершини пальцем або мишкою для створення геометрії аромату'}
                  </p>
                </div>

                {/* Radar Canvas */}
                <div className="relative w-full max-w-[360px] aspect-square flex items-center justify-center">
                  <canvas
                    ref={radarRef}
                    width={400}
                    height={400}
                    className="w-full h-full touch-none select-none cursor-grab active:cursor-grabbing"
                    onPointerDown={handleRadarPointerDown}
                    onPointerMove={handleRadarPointerMove}
                    onPointerUp={handleRadarPointerUp}
                  />
                </div>

                {/* Radar Mini Sliders */}
                <div className="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 pt-4 border-t border-white/10">
                  {(
                    ['freshness', 'sharpness', 'sillage', 'sweetness', 'formality'] as (keyof PerfumeVector)[]
                  ).map((key) => {
                    const meta = AXIS_MAP[key]
                    const val = Math.round((targetVector[key] ?? 0.5) * 100)
                    return (
                      <div
                        key={key}
                        className="bg-white/[0.03] border border-white/10 rounded-xl p-3 flex items-center justify-between gap-3 text-xs overflow-hidden"
                      >
                        <span className="text-neutral-200 font-semibold truncate w-24 shrink-0">
                          {lang === 'en' ? meta.nameEn.split(' ')[0] : meta.nameUk.split(' ')[0]}
                        </span>
                        <input
                          type="range"
                          min="5"
                          max="95"
                          value={val}
                          onChange={(e) => handleSliderChange(key, Number(e.target.value))}
                          className="flex-1 accent-[#d7b171] cursor-pointer h-1.5 bg-neutral-800 rounded min-w-0"
                        />
                        <span className="text-[#d7b171] font-mono font-bold w-10 text-right shrink-0">
                          {val}%
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* ─── TAB 4: SIDE-BY-SIDE VERSUS BATTLE ─── */}
            {activeTab === 'versus' && (
              <div className="bg-[#181622] border border-white/10 rounded-2xl p-4 md:p-6 space-y-6 shadow-2xl">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-white/10">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-[#d7b171] flex items-center gap-1.5">
                      <Swords className="w-4 h-4 text-[#d7b171]" />
                      <span>{lang === 'en' ? 'Olfactory Battle & Dual Radar' : 'Ольфакторний Side-by-Side Батл'}</span>
                    </span>
                    <p className="text-[11px] text-neutral-400 mt-0.5">
                      {lang === 'en'
                        ? 'Compare two fragrances simultaneously on the radar & analyze shared vs unique notes'
                        : 'Порівнюйте два аромати одночасно на радарі: спільні та унікальні ноти, відмінність шлейфу й сухості'}
                    </p>
                  </div>
                  {versusProduct && (
                    <button
                      type="button"
                      onClick={() => setVersusProduct(null)}
                      className="text-xs text-neutral-400 hover:text-white px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 self-start sm:self-auto"
                    >
                      {lang === 'en' ? 'Change competitor' : 'Змінити суперника'}
                    </button>
                  )}
                </div>

                {/* Fragrance Selectors Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Fragrance A (Current / Reference) */}
                  <div className="bg-white/[0.03] border border-[#d7b171]/40 rounded-xl p-3.5 space-y-2 relative overflow-hidden">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#d7b171] flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-[#d7b171]" />
                        <span>{lang === 'en' ? 'Fragrance A (Base / Anchor)' : 'Парфум А (Ціль / Еталон)'}</span>
                      </span>
                    </div>
                    {referencePerfume ? (
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-black/40 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                          {referencePerfume.photo_url || referencePerfume.photo_source_url ? (
                            <img
                              src={referencePerfume.photo_url || referencePerfume.photo_source_url || ''}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Package className="w-5 h-5 text-neutral-500" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-white truncate">
                            {localizedName(lang, referencePerfume.name_uk, referencePerfume.name_en)}
                          </p>
                          <p className="text-[10px] text-[#d7b171] truncate">{referencePerfume.brand}</p>
                          <p className="text-[11px] font-bold text-[#c9a24b] mt-0.5">
                            {formatPrice(
                              referencePerfume.price_uah ||
                                (referencePerfume.price_usd ? Math.round(referencePerfume.price_usd * 44) : 0),
                            )}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-neutral-400 py-2">
                        <span>{lang === 'en' ? 'Interactive Target Shape (Selected on Radar)' : 'Кастомний профіль радара (золотий контур)'}</span>
                      </div>
                    )}
                  </div>

                  {/* Fragrance B (Opponent / Competitor) */}
                  <div className="bg-white/[0.03] border border-[#06b6d4]/40 rounded-xl p-3.5 space-y-2 relative overflow-hidden">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#06b6d4] flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-[#06b6d4]" />
                        <span>{lang === 'en' ? 'Fragrance B (Competitor)' : 'Парфум Б (Суперник)'}</span>
                      </span>
                    </div>
                    {versusProduct ? (
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-black/40 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                          {versusProduct.photo_url || versusProduct.photo_source_url ? (
                            <img
                              src={versusProduct.photo_url || versusProduct.photo_source_url || ''}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Package className="w-5 h-5 text-neutral-500" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-white truncate">
                            {localizedName(lang, versusProduct.name_uk, versusProduct.name_en)}
                          </p>
                          <p className="text-[10px] text-[#06b6d4] truncate">{versusProduct.brand}</p>
                          <p className="text-[11px] font-bold text-[#c9a24b] mt-0.5">
                            {formatPrice(
                              versusProduct.price_uah ||
                                (versusProduct.price_usd ? Math.round(versusProduct.price_usd * 44) : 0),
                            )}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-[11px] text-neutral-400">
                          {lang === 'en' ? 'Pick a fragrance below to compare side-by-side:' : 'Оберіть аромат для порівняння з топу:'}
                        </p>
                        <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-1">
                          {scoredProducts.slice(0, 4).map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => {
                                haptic('medium')
                                setVersusProduct(p)
                              }}
                              className="px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-[11px] text-white whitespace-nowrap hover:border-[#06b6d4]"
                            >
                              {localizedName(lang, p.name_uk, p.name_en)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Radar Overlay Canvas */}
                <div className="flex flex-col items-center justify-center py-2">
                  <div className="flex items-center gap-4 text-xs font-semibold mb-2">
                    <span className="flex items-center gap-1.5 text-[#d7b171]">
                      <span className="w-3 h-0.5 bg-[#d7b171] inline-block" />
                      <span>{referencePerfume ? localizedName(lang, referencePerfume.name_uk, referencePerfume.name_en) : 'Ціль А'}</span>
                    </span>
                    {versusProduct && (
                      <span className="flex items-center gap-1.5 text-[#06b6d4]">
                        <span className="w-3 h-0.5 border-t-2 border-dashed border-[#06b6d4] inline-block" />
                        <span>{localizedName(lang, versusProduct.name_uk, versusProduct.name_en)}</span>
                      </span>
                    )}
                  </div>
                  <div className="relative w-full max-w-[340px] aspect-square flex items-center justify-center">
                    <canvas
                      ref={radarRef}
                      width={400}
                      height={400}
                      className="w-full h-full touch-none select-none"
                    />
                  </div>
                </div>

                {/* Connoisseur Versus Breakdown: Shared vs Unique Notes */}
                {versusProduct && (
                  <div className="space-y-3 pt-4 border-t border-white/10">
                    <span className="text-xs font-bold uppercase tracking-wider text-white block">
                      {lang === 'en' ? 'Olfactory Note Breakdown' : 'Ольфакторна відмінність та спільні ноти'}
                    </span>
                    {(() => {
                      const notesA = (referencePerfume?.all_notes || referencePerfume?.key_notes || []).map((n) => n.trim())
                      const notesB = (versusProduct.all_notes || versusProduct.key_notes || []).map((n) => n.trim())
                      const shared = notesA.filter((na) =>
                        notesB.some((nb) => nb.toLowerCase() === na.toLowerCase()),
                      )
                      const uniqueA = notesA.filter(
                        (na) => !notesB.some((nb) => nb.toLowerCase() === na.toLowerCase()),
                      )
                      const uniqueB = notesB.filter(
                        (nb) => !notesA.some((na) => na.toLowerCase() === nb.toLowerCase()),
                      )

                      return (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                          {/* Shared */}
                          <div className="bg-emerald-500/[0.06] border border-emerald-500/20 rounded-xl p-3 space-y-1.5">
                            <span className="text-[11px] font-bold text-emerald-400 block">
                              🌿 {lang === 'en' ? 'Shared Notes' : 'Спільні ноти'} ({shared.length})
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {shared.length > 0 ? (
                                shared.map((n, i) => (
                                  <span key={i} className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-200 text-[10px]">
                                    {n}
                                  </span>
                                ))
                              ) : (
                                <span className="text-[10px] text-neutral-500">Спільних інгредієнтів не виявлено</span>
                              )}
                            </div>
                          </div>

                          {/* Only in A */}
                          <div className="bg-[#d7b171]/[0.06] border border-[#d7b171]/20 rounded-xl p-3 space-y-1.5">
                            <span className="text-[11px] font-bold text-[#d7b171] block truncate">
                              ✨ {lang === 'en' ? 'Only in' : 'Тільки в'} {referencePerfume ? localizedName(lang, referencePerfume.name_uk, referencePerfume.name_en) : 'A'}
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {uniqueA.length > 0 ? (
                                uniqueA.map((n, i) => (
                                  <span key={i} className="px-2 py-0.5 rounded bg-[#d7b171]/15 text-[#ebd5ab] text-[10px]">
                                    {n}
                                  </span>
                                ))
                              ) : (
                                <span className="text-[10px] text-neutral-500">—</span>
                              )}
                            </div>
                          </div>

                          {/* Only in B */}
                          <div className="bg-[#06b6d4]/[0.06] border border-[#06b6d4]/20 rounded-xl p-3 space-y-1.5">
                            <span className="text-[11px] font-bold text-[#06b6d4] block truncate">
                              🔹 {lang === 'en' ? 'Only in' : 'Тільки в'} {localizedName(lang, versusProduct.name_uk, versusProduct.name_en)}
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {uniqueB.length > 0 ? (
                                uniqueB.map((n, i) => (
                                  <span key={i} className="px-2 py-0.5 rounded bg-[#06b6d4]/15 text-[#67e8f9] text-[10px]">
                                    {n}
                                  </span>
                                ))
                              ) : (
                                <span className="text-[10px] text-neutral-500">—</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* ─── TAB 5: TIME EVOLUTION PYRAMID ─── */}
            {activeTab === 'pyramid' && (
              <div className="bg-[#181622] border border-white/10 rounded-2xl p-4 md:p-6 space-y-6 shadow-2xl">
                <div className="space-y-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#d7b171] flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-[#d7b171]" />
                    <span>{lang === 'en' ? 'Fragrance Evolution Over Time' : 'Динаміка розкриття аромату в часі'}</span>
                  </span>
                  <p className="text-[11px] text-neutral-400">
                    {lang === 'en'
                      ? 'Niche fragrances transform as molecules evaporate. Select a time phase to observe changing notes & sillage.'
                      : 'Парфуми змінюються з кожною годиною. Оберіть фазу розкриття, щоб почути конкретні ноти піраміди.'}
                  </p>
                </div>

                {/* Timeline Phase Selector */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    {
                      id: 'top',
                      time: '0 – 15 хв',
                      titleUk: 'Верхні ноти (Старт)',
                      titleEn: 'Top Notes (Opening)',
                      descUk: 'Летючі цитруси, альдегіди, озон, перше враження',
                      descEn: 'Volatile citrus, aldehydes, ozone, initial burst',
                      icon: Wind,
                      accent: 'text-sky-400 border-sky-500/40 bg-sky-500/10',
                    },
                    {
                      id: 'heart',
                      time: '1 – 3 год',
                      titleUk: 'Серце аромату (Шлейф)',
                      titleEn: 'Heart Notes (Core Sillage)',
                      descUk: 'Квіти, прянощі, зелень, основний характер',
                      descEn: 'Spices, florals, aromatics, true identity',
                      icon: Sparkles,
                      accent: 'text-[#d7b171] border-[#c8a96e]/50 bg-[#c8a96e]/15',
                    },
                    {
                      id: 'base',
                      time: '4 – 10+ год',
                      titleUk: 'База & Шлейф (Стійкість)',
                      titleEn: 'Base & Drydown',
                      descUk: 'Амбра, смоли, уд, кедр, мускус, інтимна глибина',
                      descEn: 'Amber, resins, oud, cedar, musk, skin scent',
                      icon: Moon,
                      accent: 'text-amber-400 border-amber-500/40 bg-amber-500/10',
                    },
                  ].map((phase) => {
                    const isSelected = pyramidStage === phase.id
                    const Icon = phase.icon
                    return (
                      <button
                        key={phase.id}
                        type="button"
                        onClick={() => {
                          haptic('medium')
                          setPyramidStage(phase.id as any)
                          // Automatically adjust vector slightly to reflect phase characteristics
                          if (phase.id === 'top') {
                            setTargetVector((prev) => ({ ...prev, freshness: 0.25, sharpness: 0.65 }))
                          } else if (phase.id === 'heart') {
                            setTargetVector((prev) => ({ ...prev, freshness: 0.55, sweetness: 0.55 }))
                          } else if (phase.id === 'base') {
                            setTargetVector((prev) => ({ ...prev, freshness: 0.85, sweetness: 0.68, sharpness: 0.45 }))
                          }
                        }}
                        className={`flex flex-col items-start p-3.5 rounded-xl border text-left transition-all ${
                          isSelected
                            ? `${phase.accent} shadow-lg scale-[1.02]`
                            : 'bg-white/[0.03] border-white/10 hover:border-white/20'
                        }`}
                      >
                        <div className="flex items-center justify-between w-full mb-1">
                          <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-neutral-400">
                            ⏱️ {phase.time}
                          </span>
                          <Icon className="w-4 h-4 shrink-0" />
                        </div>
                        <span className="text-xs font-bold text-white mb-1">
                          {lang === 'en' ? phase.titleEn : phase.titleUk}
                        </span>
                        <span className="text-[10px] text-neutral-400 leading-tight">
                          {lang === 'en' ? phase.descEn : phase.descUk}
                        </span>
                      </button>
                    )
                  })}
                </div>

                {/* Active Fragrance Pyramid Visualizer */}
                {referencePerfume ? (
                  <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-white">
                        Піраміда для: <span className="text-[#d7b171]">{localizedName(lang, referencePerfume.name_uk, referencePerfume.name_en)}</span>
                      </span>
                      <span className="text-[10px] text-neutral-400">
                        {referencePerfume.brand}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 text-xs">
                      {/* Top Notes */}
                      <div className={`p-3 rounded-lg border transition ${pyramidStage === 'top' ? 'bg-sky-500/10 border-sky-400' : 'bg-black/20 border-white/5 opacity-70'}`}>
                        <span className="text-[10px] font-bold text-sky-300 block mb-1">
                          0-15 хв: Верхні ноти
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {(referencePerfume.pyramid?.top || referencePerfume.key_notes?.slice(0, 2) || []).map((n, i) => (
                            <span key={i} className="px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-200 text-[10px]">
                              {n}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Heart Notes */}
                      <div className={`p-3 rounded-lg border transition ${pyramidStage === 'heart' ? 'bg-[#c8a96e]/15 border-[#c8a96e]' : 'bg-black/20 border-white/5 opacity-70'}`}>
                        <span className="text-[10px] font-bold text-[#d7b171] block mb-1">
                          1-3 год: Серцеві ноти
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {(referencePerfume.pyramid?.heart || referencePerfume.dominant_chords || []).map((n, i) => (
                            <span key={i} className="px-1.5 py-0.5 rounded bg-[#c8a96e]/20 text-[#ebd5ab] text-[10px]">
                              {n}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Base Notes */}
                      <div className={`p-3 rounded-lg border transition ${pyramidStage === 'base' ? 'bg-amber-500/10 border-amber-400' : 'bg-black/20 border-white/5 opacity-70'}`}>
                        <span className="text-[10px] font-bold text-amber-300 block mb-1">
                          4-8+ год: Базові ноти
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {(referencePerfume.pyramid?.base || referencePerfume.key_notes?.slice(-2) || []).map((n, i) => (
                            <span key={i} className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 text-[10px]">
                              {n}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-3 text-xs text-neutral-400 bg-white/[0.02] border border-white/5 rounded-xl">
                    <span>💡 Оберіть парфум через пошук вгорі або натисніть на аромат у топі, щоб відкрити його повну піраміду розкриття</span>
                  </div>
                )}
              </div>
            )}

            {/* ─── TAB 5: SCENT CAPSULE BUILDER (4-SCENT WARDROBE & DISCOVERY BOX) ─── */}
            {activeTab === 'capsule' && (
              <ScentCapsuleBuilder />
            )}
        </div>

        {/* 2. Top Matches Section: Placed BELOW the visual tool in a spacious multi-column grid */}
        {isDiscoveryTab && (
        <div className="w-full space-y-4 pt-6 border-t border-white/10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-1">
            <div>
              <span className="text-xs uppercase tracking-widest text-[#d7b171] font-bold block">
                {lang === 'en' ? 'Top Scent Matches' : 'Найближчі ольфакторні відповідники'}
              </span>
              <p className="text-[11px] text-neutral-400 mt-0.5">
                {lang === 'en'
                  ? 'Fragrances ranked by proximity to your selected sensory coordinates'
                  : 'Аромати каталогу, відсортовані за спорідненістю до обраних координат'}
              </p>
            </div>
            <span className="text-xs font-semibold text-[#c8a96e] bg-[#c8a96e]/10 border border-[#c8a96e]/30 px-3 py-1 rounded-full self-start sm:self-auto">
              {scoredProducts.length > 0 ? `Топ 12 з ${scoredProducts.length}` : ''}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {scoredProducts.slice(0, 12).map((item) => {
              const pct = item.matchPct
              const pctColor =
                pct >= 88 ? 'text-emerald-400' : pct >= 70 ? 'text-[#d7b171]' : 'text-pink-400'
              const pctBg =
                pct >= 88
                  ? 'bg-emerald-500/10 border-emerald-500/30'
                  : pct >= 70
                  ? 'bg-[#c8a96e]/10 border-[#c8a96e]/30'
                  : 'bg-pink-500/10 border-pink-500/30'

              const priceUah =
                item.price_uah || (item.price_usd ? Math.round(item.price_usd * 44) : 0)

              return (
                <Link
                  key={item.id}
                  to="/catalog/$productSlug"
                  params={{ productSlug: item.slug || String(item.id) }}
                  onMouseEnter={() => setHoveredProduct(item)}
                  onMouseLeave={() => setHoveredProduct(null)}
                  className="flex flex-col justify-between bg-white/[0.04] border border-white/10 hover:border-[#c8a96e]/60 rounded-2xl p-3.5 transition duration-200 group shadow-md relative overflow-hidden"
                >
                  <div>
                    {/* Top Row: Image + Main Info + Badge */}
                    <div className="flex items-start gap-3">
                      {/* Image */}
                      <div className="w-16 h-16 rounded-xl bg-neutral-900 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                        {item.photo_url || item.photo_source_url ? (
                          <img
                            src={item.photo_url || item.photo_source_url || ''}
                            alt={item.name_uk}
                            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                            loading="lazy"
                          />
                        ) : (
                          <Package className="w-6 h-6 text-neutral-500" />
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0 pr-1">
                        {item.brand && (
                          <p className="text-[10px] uppercase tracking-wider text-[#c8a96e] font-semibold truncate mb-0.5">
                            {item.brand}
                          </p>
                        )}
                        <p className="text-xs font-bold text-white line-clamp-2 group-hover:text-[#ebd5ab] transition">
                          {localizedName(lang, item.name_uk, item.name_en)}
                        </p>
                        <p className="text-xs font-bold text-[#c9a24b] mt-1">
                          {formatPrice(priceUah)}
                        </p>
                      </div>

                      {/* Match Badge */}
                      <div className={`px-2 py-1 rounded-xl border text-center shrink-0 ${pctBg}`}>
                        <span className={`block text-sm font-bold font-mono leading-none ${pctColor}`}>
                          {pct}%
                        </span>
                        <span className="block text-[8px] uppercase tracking-wider text-neutral-400 mt-0.5">
                          {lang === 'en' ? 'Match' : 'Збіг'}
                        </span>
                      </div>
                    </div>

                    {/* Middle: Chords & Notes Pills */}
                    {(item.dominant_chords || item.key_notes) && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {item.dominant_chords?.slice(0, 2).map((c, i) => (
                          <span
                            key={`c-${i}`}
                            className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-[#bc9050]/15 text-[#d7b171]"
                          >
                            #{c}
                          </span>
                        ))}
                        {item.key_notes?.slice(0, 1).map((n, i) => (
                          <span
                            key={`n-${i}`}
                            className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300"
                          >
                            🌿 {n}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Match Reason */}
                    {item.match_reason && (
                      <p className="text-[10px] leading-tight text-neutral-400 mt-2 line-clamp-2 opacity-80">
                        {item.match_reason}
                      </p>
                    )}
                  </div>

                  {/* Bottom: Action buttons */}
                  <div className="mt-3 pt-2 border-t border-white/5 flex items-center justify-between gap-1 text-[11px]">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          haptic('medium')
                          setVersusProduct(item)
                          handleTabChange('versus')
                        }}
                        className="px-2 py-1 rounded-md bg-white/5 hover:bg-[#06b6d4]/20 hover:text-[#06b6d4] text-neutral-400 font-semibold text-[10px] flex items-center gap-1 transition"
                        title={lang === 'en' ? 'Compare in Versus Battle' : 'Порівняти в Батлі'}
                      >
                        <Swords className="w-3 h-3" />
                        <span>{lang === 'en' ? 'Versus' : 'Батл'}</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          haptic('light')
                          setReferencePerfume(item)
                          handleTabChange('pyramid')
                        }}
                        className="px-2 py-1 rounded-md bg-white/5 hover:bg-amber-500/20 hover:text-amber-300 text-neutral-400 font-semibold text-[10px] flex items-center gap-1 transition"
                        title={lang === 'en' ? 'View Pyramid' : 'Піраміда'}
                      >
                        <Clock className="w-3 h-3" />
                        <span>{lang === 'en' ? 'Pyramid' : 'Піраміда'}</span>
                      </button>
                    </div>

                    <span className="text-neutral-400 group-hover:text-[#c8a96e] transition flex items-center gap-0.5">
                      <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition text-[#c8a96e]" />
                    </span>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
        )}
      </div>
    </div>
  )
}
